import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TalkToEngineerModal } from "@/components/TalkToEngineerModal";
import { PaymentMethodModal } from "@/components/PaymentMethodModal";
import { PurchaseModal } from "@/components/PurchaseModal";
import { ConfirmUnlockModal } from "@/components/ConfirmUnlockModal";
import { PaywallSafetyNet } from "@/components/PaywallSafetyNet";
import { equipmentRow, capexTech, sourceName, sectorName } from "@/lib/resultsFr";
import { HowItWorks } from "@/components/HowItWorks";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SensitivityPanel } from "@/components/SensitivityPanel";
// S5f-a C3 item 3 — the not-scored marker is a truthy object; interpolating it
// rendered "[object Object] yr" in the payback KPI.
import { comparisonYears, comparisonLong, isNotScored, noComparisonLong } from "@/lib/comparison";
import { firmPill, firmTileOk, FIRM_PILL_BG, FIRM_PILL_FG } from "@/lib/firmPill";
import { AdjustMixPanel, MixCompare } from "@/components/AdjustMixPanel";
import { FrontierStrip } from "@/components/FrontierStrip";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT, getLocale } from "@/lib/i18n";
import { recordReportVersion } from "@/lib/reportVersion";
import { applicationLabel } from "@/lib/application";
import { roadmapLeadNote } from "@/lib/roadmap";
import { localisePermitting } from "@/lib/permittingI18n";
import { WhySizePopover, buildThermalMixPicks } from "@/lib/whySize";
import { canonicalPriority } from "@/lib/priority";
import { emissionFactorsFromConfig } from "@/lib/mixMetrics";
import { recommendedThermalPlant, plantLabel } from "@/lib/thermalSeed";
import { firmBasisNote, firmBasisPair, generatorDeratePct, ANALYSIS_YEARS } from "@/lib/firmBasis";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { Lock, Download, Save, RotateCcw, ShieldCheck, Sparkles, ArrowLeft, ArrowRight, PhoneCall, FileText, CreditCard, Ticket, Info } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { num, money, moneyK, unit, perUnit, perYr, pct } from "@/lib/numfmt";

const usd = (n) => `${money(n || 0, 0)}`;
const fmt = (n) => num(n || 0, 1);

const MIX_COLORS = { solar: "#65B037", wind: "#4CA6A8", diesel: "#33403B", hfo: "#6E7A74", gas: "#F6611C", grid: "#1A6F2B" };

// ── iter44m — Carbon footprint score ────────────────────────────────────
// Screening-level annual CO2 (t/yr) from the engine result's energy mix.
// Diesel emissions use the same engine coefficients as the ESG block in
// the paid PDF (0.28 L/kWh × 2.7 kg CO2/L). Solar / battery / wind /
// grid-import (assumed low-carbon by default) contribute zero direct.
// Gas thermal emissions come from `thermal_extras.totals.co2_added_t_yr`
// (already gross of any CHP boiler credit — we subtract the credit here
// to match the "net emitted" number the PDF prints). Grid emissions use
// a 0.25 kg/kWh factor by default (comparable to a low-carbon regional
// grid) — buyers wanting a stricter grid factor override it in admin.
const CARBON_KG_PER_L_DIESEL = 2.7;   // engine coefficient
const CARBON_KG_PER_L_HFO = 3.15;
const CARBON_L_PER_KWH_DIESEL = 0.28; // engine coefficient
const CARBON_L_PER_KWH_HFO = 0.24;
const CARBON_GRID_KG_PER_KWH = 0.25;


// ONE TCO — the rate note reads the engine's nominal rate and escalation (financials).
const rateNote = (tpl, fin) => {
  const r = Number(fin?.discount_rate), e = Number(fin?.opex_escalation);
  const pct = (x, d) => num(x * 100, d, d);
  return tpl.replace("{rate}", pct(r, 0)).replace("{real}", pct((1 + r) / (1 + e) - 1, 1)).replace("{esc}", pct(e, 1));
};

// iter45as4c commit (iii) — the "Hybrid pays back in N yr" chip and its
// `scenarioBreakEvenYear` helper are GONE (D17a/D18). With one cost kernel
// both cards were the same plant on a full_thermal winner, so the chip read
// as a plant paying itself back. Not repointed at the runner-up or at
// diesel-only: those are different comparisons and neither is ruled.

// iter45aq commit 1 — the frontend computes NO carbon. It reads what the
// engine computed. The JavaScript copy of the emission-factor table that
// used to live here hardcoded Nigeria's grid factor for every country, so a
// Kenyan buyer was shown 299.5 gCO2/kWh MEDIUM against a true 68.1 LOW. Band
// thresholds stay here because the engine does not surface a band on the
// result; they match backend/emissions.py.
const BAND_LOW_MAX = 200;
const BAND_MEDIUM_MAX = 500;

// Operational CO2/yr for the recommended mix, in tonnes/yr. `null` when the
// engine did not compute it (legacy saved projects, and the /recompute path
// until commit 1b) — callers MUST render the absent state, never 0.
const hybridCO2TonnesYr = (r) => {
  const v = r?.model?.co2_t_yr;
  return v == null ? null : Number(v);
};

const thermalCO2TonnesYr = (r) => {
  const totals = r?.model?.thermal_extras?.totals || {};
  const net = totals.co2_added_t_yr_net;
  if (typeof net === "number") return net;
  return Number(totals.co2_added_t_yr || 0)
       - Number((r?.model?.thermal_extras?.chp || {}).avoided_boiler_co2_t_yr || 0);
};

const carbonFromResult = (r) => {
  const m = r?.model || {};
  const tonnes = m.co2_t_yr;
  const intensityG = m.co2_intensity_g_kwh;
  if (tonnes == null || intensityG == null) {
    // F4 — absent, not zero. No band is asserted for a figure we don't have.
    return { available: false, tonnes: null, intensityG: null,
              rating: null, color: "var(--muted)", pillBg: "transparent" };
  }
  let rating = "low", color = "#1A6F2B", pillBg = "#E8F5E9";
  if (intensityG > BAND_MEDIUM_MAX) {
    rating = "high"; color = "#B00020"; pillBg = "#FDECEC";
  } else if (intensityG > BAND_LOW_MAX) {
    rating = "medium"; color = "#B26B25"; pillBg = "#FDECD9";
  }
  return { available: true, tonnes: Number(tonnes),
            intensityG: Number(intensityG), rating, color, pillBg };
};

// iter44o — Concrete "how to drop into Low" tweak suggestion.
// Only fires when the current rating is Medium or High. Sizing is a
// screening-level heuristic — no engine call — so it must be conservative
// (round up), directional and worded as "try", not a promise. Returns
// null on Low so callers can skip the prompt block entirely.
const carbonReductionTweak = (result, score, t) => {
  if (!result || !score || !score.available || score.rating === "low") return null;
  const m = result.model || {};
  const src = m.energy_per_source_kwh || {};
  const annualKwh = Number(m.annual_energy_kwh || 0);
  if (annualKwh <= 0) return null;
  // Target = the top of the Low band (200 gCO2/kWh). Compute the annual
  // kg of CO2 we need to strip out to get there.
  const targetIntensityG = 200;
  const excessKg = ((score.intensityG - targetIntensityG) * annualKwh) / 1000;
  if (excessKg <= 0) return null;
  // iter45an Prompt G — factor 0.72 kg/kWh matches backend/emissions.py EF.
  const dieselKwhToOffset = excessKg / 0.72;
  const teEnabled = m.thermal_extras?.picks?.length > 0;
  const chpOn = m.thermal_extras?.chp?.enabled;
  const dieselKwh = Number(src.diesel || 0);
  const gasKwh = Number(src.gas_thermal || src.gas || 0);
  const gridKwh = Number(src.grid || 0);
  // Fallback `t` (used by unit tests / older callers that don't pass one).
  const tr = t || ((k) => k);

  // Pick the biggest lever first — matches customer's "one concrete tweak" ask.
  //  1. Thermal on but CHP off → enabling CHP with a real heat load is the
  //     highest-impact single click a buyer can make.
  //  2. Diesel-driven emissions → size up solar+battery to time-shift diesel
  //     kWh out of the mix.
  //  3. Grid-heavy on a dirty grid → suggest a renewable PPA (indicative only).
  if (teEnabled && !chpOn && gasKwh > 0) {
    // §8.6 — the tip said "your gas engine" on an OCGT plant. Name the
    // technology that is actually there, and state where the tweak lands.
    const hostTech = (m.thermal_extras?.picks || [])
      .reduce((a, b) => (Number(b.installed_kw || 0) > Number(a?.installed_kw || 0) ? b : a), null)?.tech
      || m.thermal_tech || "gas_engine";
    return {
      lever: "chp",
      copy: tr("results.carbon.tweak.chp_copy")
        .replace("{{tech}}", tr(`results.carbon.tech.${hostTech}`))
        .replace("{{projected}}", num(Math.round(score.intensityG * 0.8), 0)),
      badge: tr("results.carbon.tweak.chp_badge"),
      patch: { chp: true },
    };
  }
  if (dieselKwh > 0 && dieselKwhToOffset > 0) {
    // Round to sensible chunks so the copy reads well: <500 → 100, <5k → 500, else 1k.
    const roundTo = (x, step) => Math.ceil(x / step) * step;
    // Battery sized to cover ~1 diesel-hour of displaced dispatch per day
    // (365 cycles/yr rule of thumb). Ceiling to nearest 100 kWh.
    const batteryKwh = roundTo(dieselKwhToOffset / 365, 100);
    // Solar sized for 1600 kWh/kWp/yr (median West Africa figure).
    const solarKwp = roundTo(dieselKwhToOffset / 1600, 100);
    const dieselMwh = Math.round(dieselKwhToOffset / 1000);
    return {
      lever: "solar_battery",
      copy: tr("results.carbon.tweak.solar_battery_copy")
        .replace("{{battery}}", num(batteryKwh, 3))
        .replace("{{solar}}", num(solarKwp, 3))
        .replace("{{diesel}}", num(dieselMwh, 3)),
      badge: tr("results.carbon.tweak.solar_battery_badge").replace("{{battery}}", num(batteryKwh, 3)),
      patch: { batteryKwhAdd: batteryKwh, solarKwpAdd: solarKwp },
    };
  }
  if (gridKwh > 0 && gridKwh / annualKwh > 0.2) {
    return {
      lever: "renewable_ppa",
      copy: tr("results.carbon.tweak.ppa_copy"),
      badge: tr("results.carbon.tweak.ppa_badge"),
      patch: null,
    };
  }
  return null;
};

// Presentational card. Kept in-file since it's Results-page-specific and
// small — no reuse elsewhere yet.
const CarbonScoreCard = ({ recommended, adjusted, onTryTweak, t }) => {
  const rec = carbonFromResult(recommended);
  const adj = adjusted ? carbonFromResult(adjusted) : null;
  const ratingLabel = (r) => t(`results.carbon.rating.${r}`);
  const badge = (score) => (
    <span className="pill uppercase text-[10px] font-bold"
           style={score.available
             ? { background: score.pillBg, color: score.color }
             : { background: "transparent", color: "var(--muted)",
                  border: "1px dashed var(--rule)" }}
           data-testid={score.available
             ? `carbon-rating-${score.rating}`
             : "carbon-rating-unavailable"}>
      {score.available ? ratingLabel(score.rating) : "—"}
    </span>
  );
  const bar = (score) => (
    // Continuous colour bar 0–800 gCO2/kWh with a tick marker at the
    // score's intensity so buyers see where they land at a glance.
    <div className="mt-4 h-2 rounded relative" style={{ background: "var(--paper)" }}>
      <div className="absolute h-2 rounded" style={{ left: "0%", width: "100%",
        background: "linear-gradient(90deg, #1A6F2B 0%, #1A6F2B 25%, #B26B25 25%, #B26B25 62%, #B00020 62%, #B00020 100%)" }} />
      {score.available && (
        <div className="absolute -top-1 h-4 w-0.5"
              style={{ background: "var(--char)",
                        left: `${Math.min(100, (score.intensityG / 800) * 100)}%` }} />
      )}
    </div>
  );

  return (
    <section className="card p-6 md:p-8" data-testid="block-carbon-score">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline">{t("results.carbon.overline")}</div>
          <h2 className="font-display text-2xl mt-1" data-testid="carbon-tonnes-recommended">
            {rec.available ? fmt(rec.tonnes) : "—"} <span className="text-lg font-normal" style={{ color: "var(--muted)" }}>{t("results.carbon.unit")}</span>
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {t("results.carbon.intensity")} <span className="font-mono" data-testid="carbon-intensity-recommended">{rec.available ? fmt(rec.intensityG) : "—"} {t("results.carbon.intensity_unit")}</span> · {t("results.carbon.recommended_mix")}
          </p>
        </div>
        {badge(rec)}
      </div>
      {bar(rec)}
      <div className="mt-2 flex justify-between text-xs font-mono" style={{ color: "var(--muted)" }}>
        <span>{t("results.carbon.scale_low")}</span>
        <span>{t("results.carbon.scale_med")}</span>
        <span>{t("results.carbon.scale_high")}</span>
      </div>
      {adj && (
        <div className="mt-4 pt-4 flex items-center justify-between gap-3 flex-wrap"
              style={{ borderTop: "1px dashed var(--rule)" }}
              data-testid="carbon-your-configuration">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--muted)" }}>
              {t("results.carbon.your_config")}
            </div>
            <div className="text-lg font-display mt-0.5" data-testid="carbon-tonnes-adjusted">
              {adj.available ? fmt(adj.tonnes) : "—"} <span className="text-xs font-normal" style={{ color: "var(--muted)" }}>{t("results.carbon.unit")}</span>
              {(() => {
                if (!adj.available || !rec.available) return null;
                const delta = adj.tonnes - rec.tonnes;
                if (Math.abs(delta) < 0.5) return null;
                const better = delta < 0;
                return (
                  <span className="ml-2 text-xs font-mono font-bold"
                          style={{ color: better ? "#1A6F2B" : "#B00020" }}
                          data-testid="carbon-delta">
                    {better ? "▼" : "▲"} {fmt(Math.abs(delta))} t
                  </span>
                );
              })()}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
              {t("results.carbon.intensity")} <span className="font-mono" data-testid="carbon-intensity-adjusted">{adj.available ? fmt(adj.intensityG) : "—"} {t("results.carbon.intensity_unit")}</span>
            </div>
          </div>
          {badge(adj)}
        </div>
      )}
      {/* iter44o — Reduction tweak prompt. Only fires when the shown
           score is Medium or High. Prefers the buyer-adjusted result when
           present (they're actively iterating). Copy is written to read
           as advice, not a promise — screening-level only. */}
      {(() => {
        const activeResult = adj ? adjusted : recommended;
        const activeScore = adj || rec;
        const tweak = carbonReductionTweak(activeResult, activeScore, t);
        if (!tweak) return null;
        return (
          <div className="mt-4 pt-4 rounded-b flex items-start gap-3 flex-wrap"
                style={{ borderTop: "1px dashed var(--rule)" }}
                data-testid="carbon-reduction-tweak">
            <div className="pt-0.5">
              <Sparkles size={14} style={{ color: activeScore.color }} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[10px] font-bold uppercase tracking-wider"
                       style={{ color: activeScore.color }}>
                  {/* §8.6 — 550 × 0.8 = 440 g is still Medium, so the headline
                       only promises the band the lever actually reaches. */}
                  {t(tweak.lever === "solar_battery"
                       ? "results.carbon.drop_into_low"
                       : "results.carbon.reduce_emissions")}
                </div>
                <span className="pill uppercase text-[9px] font-bold"
                        style={{ background: activeScore.pillBg, color: activeScore.color }}
                        data-testid={`carbon-tweak-badge-${tweak.lever}`}>
                  {tweak.badge}
                </span>
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--body)" }}
                  data-testid="carbon-tweak-copy">
                {tweak.copy}
              </p>
              {/* iter44p — one-click apply. The button is only surfaced
                   for the two levers we can automate (solar+battery, CHP);
                   the renewable-PPA lever stays advisory since it's a
                   procurement action outside the panel. */}
              {onTryTweak && (tweak.lever === "solar_battery" || tweak.lever === "chp") && (
                <button
                  type="button"
                  onClick={() => onTryTweak(tweak)}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-medium text-xs transition-colors"
                  style={{ background: activeScore.color, color: "#fff" }}
                  data-testid={`carbon-tweak-apply-${tweak.lever}`}
                >
                  <Sparkles size={12} /> {t("results.carbon.tweak.try_it")}
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </section>
  );
};

// iter45y — Save comparison as PDF. Posts the current input+result to the
// public /api/scenarios/compare.pdf endpoint (free tier), then downloads
// the returned bytes as a file. Kept as a tiny local component so we can
// track its own "building…" state without polluting the parent's render.
const SaveCompareBtn = ({ buildBody, slug, t }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="pill hover:opacity-80"
      style={{ background: "#233333", color: "#F7F5EE", border: "1px solid #233333", cursor: busy ? "wait" : "pointer" }}
      data-testid="save-compare-pdf"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const resp = await api.post("/scenarios/compare.pdf", buildBody(), { responseType: "blob" });
          const blob = new Blob([resp.data], { type: "application/pdf" });
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `${slug || "scenario"}-comparison.pdf`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          URL.revokeObjectURL(a.href);
        } catch (err) {
          console.warn("[compare-pdf] failed", err?.message);
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? t("results.thermal.save_compare_pdf_pending") : `⇩ ${t("results.thermal.save_compare_pdf")}`}
    </button>
  );
};

export const Results = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const runId = sp.get("run");
  const { user } = useAuth();
  const t = useT();
  const locale = getLocale();
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState({ report: 99, bundle: 375, credits: 5, tiers: [] });
  const [emissionFactors, setEmissionFactors] = useState(null);
  const [talkOpen, setTalkOpen] = useState(false);
  const [busySave, setBusySave] = useState(false);
  const [savedProjectId, setSavedProjectId] = useState(null);
  const [paidUnlocked, setPaidUnlocked] = useState(false);
  const [payModal, setPayModal] = useState({ open: false, pkg: null, projectId: null });
  const [newPurchaseOpen, setNewPurchaseOpen] = useState(false);
  const [confirmUnlockOpen, setConfirmUnlockOpen] = useState(false);
  // iter45ac — Reconfigure now shows a confirmation dialog before wiping
  // the wizard back to defaults (buyer's request: "Rebuild project from the
  // beginning" pop-up). The lightweight "Back" arrow is the escape hatch
  // for tweaking a single input without losing everything else.
  const [reconfirmOpen, setReconfirmOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [adjustedResult, setAdjustedResult] = useState(null);
  // One panel (RULED): the alternative card's "Use this plant in my configuration" request.
  const [usePlantRequest, setUsePlantRequest] = useState(null);

  const [adjustedOverrides, setAdjustedOverrides] = useState(null);
  // iter45al — Buyer Config Save. `configSavedAt` toggles the Save button
  // label from "Save config" to "Saved" as visual confirmation. `staleOverrides`
  // populates a banner when the current engine cannot honor one of the buyer's
  // previously saved edits (drop-with-notice, never silent baseline fallback).
  const [configSavedAt, setConfigSavedAt] = useState(null);
  const [staleOverrides, setStaleOverrides] = useState([]);
  // iter44p — one-click "Try it" from the carbon reduction prompt. Bumps
  // iter45v — Handler for "Try it" on the carbon reduction prompt.
  const [applyTweakRequest, setApplyTweakRequest] = useState(null);
  // iter45w — Buyer's preferred scenario flag. Initialized from the engine
  // default (`scenarios.preferred`, always "recommended" at first). Flipping
  // is UI-only until the buyer pays — we mirror the change into
  // sessionStorage.last_run so paid PDF generation reads it after purchase.
  const [preferredScenario, setPreferredScenario] = useState("recommended");
  // iter45y — Free-text buyer rationale (500 char cap). Session-only until
  // the project is saved. Mirrored into result.scenarios.notes so the
  // compare PDF picks it up.
  const [scenarioNotes, setScenarioNotes] = useState("");
  // iter45y — Gas-price slider: `gasPricePreview` is the drag value used
  // for the instant local scaling. `gasPriceLive` is the last authoritative
  // recompute price (fires 250ms after the last drag tick — iter45z).
  // `gasPriceRecomputing` shows the "Recalculating…" pill while the
  // recompute is in flight.
  const [gasPricePreview, setGasPricePreview] = useState(null);
  const [gasPriceLive, setGasPriceLive] = useState(null);
  const [gasPriceRecomputing, setGasPriceRecomputing] = useState(false);
  // iter45z — Debounce handle: each onChange resets the timer; recompute
  // fires only after 250 ms of idleness. Prevents recompute spam when a
  // buyer sweeps the handle end-to-end.
  const gasPriceDebounceRef = useRef(null);
  // iter45z — Clear any pending debounced recompute on unmount so we don't
  // ping the API after the buyer's already left the page.
  useEffect(() => () => {
    if (gasPriceDebounceRef.current) clearTimeout(gasPriceDebounceRef.current);
  }, []);
  // iter45aa — Rate-card preset chips share this helper with the slider's
  // debounced onChange. Chips call it with `immediate=true` (bypass the
  // 250 ms debounce because a click is a deliberate commit); the slider
  // keeps the debounce so aggressive sweeps still only fire once.
  const runGasPriceRecompute = async (newPrice) => {
    if (newPrice === gasPriceLive) return;
    setGasPriceRecomputing(true);
    try {
      const nextInput = {
        ...input,
        thermal_selection: {
          ...(input?.thermal_selection || {}),
          fuel_gas: {
            ...(input?.thermal_selection?.fuel_gas || {}),
            delivered_price_per_gj: newPrice,
          },
        },
      };
      const j = await api.post("/configurator/recompute", {
        input: nextInput,
        overrides: adjustedOverrides || null,
        thermal_selection: nextInput.thermal_selection,
        run_id: runId,
        project_id: savedProjectId,
      });
      const newResult = j?.data?.result;
      if (newResult?.scenarios?.thermal_baseload) {
        newResult.scenarios.preferred = preferredScenario;
        newResult.scenarios.notes = scenarioNotes;
        setData((d) => ({ ...(d || {}), input: nextInput, result: newResult }));
        try {
          const raw = sessionStorage.getItem("last_run");
          const parsed = raw ? JSON.parse(raw) : { input: nextInput, result: newResult };
          parsed.input = nextInput;
          parsed.result = newResult;
          sessionStorage.setItem("last_run", JSON.stringify(parsed));
        } catch (_e) { /* no-op */ }
      }
    } catch (_err) { /* no-op */ }
    finally {
      setGasPriceLive(newPrice);
      setGasPriceRecomputing(false);
    }
  };
  const flipPreferred = (next) => {
    setPreferredScenario(next);
    try {
      const raw = sessionStorage.getItem("last_run");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.result?.scenarios) {
          parsed.result.scenarios.preferred = next;
          sessionStorage.setItem("last_run", JSON.stringify(parsed));
        }
      }
    } catch (e) { /* no-op */ }
  };
  // Mirror scenario notes into sessionStorage so a page refresh
  // preserves the buyer's rationale (matches the Preferred toggle
  // persistence behaviour).
  const updateNotes = (v) => {
    const clipped = String(v || "").slice(0, 500);
    setScenarioNotes(clipped);
    try {
      const raw = sessionStorage.getItem("last_run");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.result?.scenarios) {
          parsed.result.scenarios.notes = clipped;
          sessionStorage.setItem("last_run", JSON.stringify(parsed));
        }
      }
    } catch (e) { /* no-op */ }
  };
  const handleAdjusted = (result, overrides) => {
    setAdjustedResult(result);
    setAdjustedOverrides(result ? overrides : null);
  };

  // iter44p — Handler for "Try it" on the carbon reduction prompt.
  // Bumps the applyRequest id so the AdjustMixPanel's useEffect refires
  // even on identical consecutive patches, then scrolls the panel into
  // view and pops a toast so the buyer knows to click Recalculate (or
  // sees the auto-recalc land).
  const handleTryTweak = (tweak) => {
    if (!tweak || !tweak.patch) return;
    setApplyTweakRequest({ id: Date.now(), patch: tweak.patch, lever: tweak.lever });
    // Wait a tick for the panel to receive the patch, then scroll it into view.
    setTimeout(() => {
      const el = document.querySelector("[data-testid=adjust-mix-panel]");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    track("carbon_tweak_applied", { lever: tweak.lever });
    toast.success(t("results.carbon.applied_toast"));
  };
  // Payload that always reflects the current "config the user wants in their report":
  // either the adjusted result (if they hit Recalculate) or the recommended one.
  // iter45m — When the buyer came in via the "Re-edit configuration" flow
  // from My Projects, the source project_id is forwarded here so the
  // backend replaces the original locked row instead of inserting a
  // duplicate. Once the buyer has already saved once in this session
  // (savedProjectId set), subsequent save calls skip the replace hint
  // and just target the already-saved row.
  const buildSavePayload = () => {
    // iter45w — Fold the buyer's preferred-scenario flip into the saved
    // payload so the paid PDF picks up their choice (the initial engine
    // default is always "recommended" — the buyer may have flipped to
    // "thermal_baseload" via the compare-block toggle).
    const activeResult = adjustedResult || r;
    const withPreferred = activeResult?.scenarios
      ? { ...activeResult, scenarios: {
              ...activeResult.scenarios,
              preferred: preferredScenario,
              // iter45y — carry the buyer's rationale into the saved
              // project so the paid PDF exec summary can echo it.
              notes: scenarioNotes || activeResult.scenarios.notes || "",
          } }
      : activeResult;
    return {
      name: input?.project_name || `${input?.sector} · ${input?.country}`,
      input,
      result: withPreferred,
      overrides: adjustedResult ? adjustedOverrides : null,
      recommended_result: adjustedResult ? r : null,
      configuration_source: adjustedResult ? "adjusted" : "recommended",
      replace_project_id: !savedProjectId ? (data?.replace_project_id || null) : null,
    };
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/config").then((r) => {
      if (cancelled) return;
      setPricing({ report: r.data.report_price_usd, bundle: r.data.bundle_price_usd, credits: r.data.bundle_credits, tiers: r.data.pricing_tiers || [] });
      // D1 — per-tech emission factors, authored by backend/emissions.py.
      setEmissionFactors(emissionFactorsFromConfig(r.data));
    }).catch((err) => console.debug("[results] pricing fetch skipped", err?.message));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem("last_run");
    if (raw) {
      const parsed = JSON.parse(raw);
      setData(parsed);
      // iter45w — initialise preferred flag from saved run.
      const pref = parsed?.result?.scenarios?.preferred;
      if (pref) setPreferredScenario(pref);
      // iter45y — restore scenario notes + last gas price so a reload
      // doesn't drop the buyer's context.
      const savedNotes = parsed?.result?.scenarios?.notes;
      if (typeof savedNotes === "string") setScenarioNotes(savedNotes);
      const savedGas = parsed?.input?.thermal_selection?.fuel_gas?.delivered_price_per_gj;
      if (typeof savedGas === "number") {
        setGasPricePreview(savedGas);
        setGasPriceLive(savedGas);
      }
      return;
    }
    if (runId) {
      api.get(`/runs/${runId}`)
        .then((r) => setData({ input: r.data.input, result: r.data.result }))
        .catch((err) => {
          console.warn("[results] run fetch failed", err?.message);
          navigate("/configurator");
        });
    } else {
      navigate("/configurator");
    }
  }, [runId, navigate]);

  // iter45al — Fire on project load. If the project has saved overrides,
  // restore them AND surface any stale-override notices the engine returned.
  // Must sit above the `if (!data) return null` — hooks can't be conditional.
  useEffect(() => {
    if (!savedProjectId) return;
    let cancelled = false;
    (async () => {
      try {
        const rr = await api.get(`/projects/${savedProjectId}/config-restore`);
        if (cancelled) return;
        if (!rr.data.has_saved) return;
        setConfigSavedAt(rr.data.saved_at || null);
        if ((rr.data.stale_overrides || []).length > 0) {
          setStaleOverrides(rr.data.stale_overrides);
        }
        if (rr.data.result) {
          setAdjustedResult(rr.data.result);
          setAdjustedOverrides(rr.data.overrides || null);
        }
      } catch {
        /* silent — config-restore is best-effort */
      }
    })();
    return () => { cancelled = true; };
  }, [savedProjectId]);

  if (!data) return null;
  const { input, result: r } = data;
  const m = r.model || {};
  const cap = r.capex || {};
  const opex = r.opex || {};
  const fin = r.financials || {};
  // §12 — the engine's schedule. The page prints it; it computes no month.
  const sched = r.schedule || {};
  const codLeadNote = roadmapLeadNote(sched, t);

  // iter45h — Popover picks for the "Gas recip" MixTile on the
  // Recommended card. Compare each recommended pick against itself so
  // buyerNotes stay empty — only the auto reasoning renders in grey.
  const recThermalPicks = m.thermal_extras?.picks || [];
  // D3 — on a full-thermal recommendation the Δ baseline is the RECOMMENDED
  // plant. `thermal_extras.picks` is the wizard's step-7 selection, so every
  // chip and buyer note on the adjusted card was measured against a plant the
  // engine did not recommend.
  const recIsFullThermal = !!(r?.scored_candidate?.kind === "full_thermal"
                              || r?.candidates?.chosen_kind === "full_thermal"
                              || r?.scenarios?.winner_is_full_thermal);
  const recPlant = recommendedThermalPlant(m);
  const thermalBaselinePicks = (recIsFullThermal && recPlant)
    ? [{ tech: recPlant.tech, count: recPlant.count, unit_mw: recPlant.unit_mw }]
    : recThermalPicks;
  const recTilePicks = buildThermalMixPicks(recThermalPicks, recThermalPicks, locale);
  const energyPerSource = m.energy_per_source_kwh || {};
  const mixData = Object.entries(energyPerSource)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: sourceName(k), value: v, color: MIX_COLORS[k] || "#999" }));

  const capexBars = (cap.breakdown || [])
    .filter((b) => b.capex_usd > 0)
    .map((b) => { const n = capexTech(b.tech); return { name: n.length > 22 ? n.slice(0, 22) + "…" : n, value: b.capex_usd }; });

  const save = async () => {
    if (!user) {
      toast.info(t("results.signin_save"));
      navigate("/login");
      return;
    }
    setBusySave(true);
    try {
      const rr = await api.post("/projects", buildSavePayload());
      setSavedProjectId(rr.data.id);
      toast.success(adjustedResult ? t("results.saved_adjusted") : t("results.saved_ok"));
    } catch {
      toast.error(t("results.save_failed"));
    } finally {
      setBusySave(false);
    }
  };

  const buyReport = async (pkg) => {
    if (!user) {
      toast.info(t("results.signin_buy"));
      navigate("/login");
      return;
    }
    let projectId = savedProjectId;
    if (!projectId) {
      setBusySave(true);
      try {
        const rr = await api.post("/projects", buildSavePayload());
        projectId = rr.data.id;
        setSavedProjectId(projectId);
      } catch {
        toast.error(t("results.save_failed_project"));
        setBusySave(false);
        return;
      }
      setBusySave(false);
    }
    setPayModal({ open: false, pkg: null, projectId: null });
    // Stage 1: use the new purchase pop-up. If user has credits, prefer 1-credit confirmation.
    if ((user?.credits || 0) > 0) {
      setConfirmUnlockOpen(true);
    } else {
      setNewPurchaseOpen(true);
    }
  };

  const onPaid = (info) => {
    setPaidUnlocked(true);
    track("report_purchase", { method: info.provider, package_id: info.package_id, project_id: payModal.projectId });
    if (info.package_id === "single_report" && payModal.projectId) {
      // Small delay so backend fulfillment lands before the report page loads
      setTimeout(() => navigate(`/report/${payModal.projectId}`), 400);
    } else if (info.package_id?.startsWith("credit_bundle_")) {
      const credits = Number(info.package_id.split("_").pop() || 0);
      toast.success(credits === 1 ? t("results.credit_added_1") : t("results.credit_added").replace("{{n}}", credits));
    }
  };

  const unlockWithCredit = async () => {
    let projectId = savedProjectId;
    if (!projectId) {
      setBusySave(true);
      try {
        const rr = await api.post("/projects", buildSavePayload());
        projectId = rr.data.id;
        setSavedProjectId(projectId);
      } catch { toast.error(t("results.save_failed_project")); setBusySave(false); return; }
      setBusySave(false);
    }
    try {
      await api.post(`/projects/${projectId}/unlock-with-credit`);
      await recordReportVersion(projectId, "credit");
      toast.success(t("results.credit_unlocked"));
      setPaidUnlocked(true);
      track("report_purchase", { method: "credit", project_id: projectId });
      navigate(`/report/${projectId}`);
    } catch (e) {
      if (e.response?.status === 402) toast.error(t("results.no_credits"));
      else toast.error(t("results.unlock_failed"));
    }
  };

  // iter45al — Buyer Config Save handlers.
  const handleSaveConfig = async ({ overrides, thermal_selection }) => {
    if (!user) { navigate("/login?next=/results?run=" + runId); return; }
    let projectId = savedProjectId;
    if (!projectId) {
      try {
        const rr = await api.post("/projects", buildSavePayload());
        projectId = rr.data.id;
        setSavedProjectId(projectId);
      } catch { toast.error(t("results.save_failed_project")); return; }
    }
    try {
      const r = await api.post(`/projects/${projectId}/overrides`,
                                 { overrides, thermal_selection });
      setConfigSavedAt(r.data.saved_at);
      toast.success(t("results.config_saved"));
      track("buyer_config_saved", { project_id: projectId });
    } catch {
      toast.error(t("results.save_failed_project"));
    }
  };

  const redeemPromo = async () => {
    if (!user) { navigate("/login?next=/results?run=" + runId); return; }
    if (!promoCode.trim()) { toast.error(t("results.enter_promo")); return; }
    let projectId = savedProjectId;
    if (!projectId) {
      setPromoBusy(true);
      try {
        const rr = await api.post("/projects", buildSavePayload());
        projectId = rr.data.id;
        setSavedProjectId(projectId);
      } catch {
        toast.error(t("results.save_failed_project"));
        setPromoBusy(false);
        return;
      }
    }
    setPromoBusy(true);
    try {
      const { data: redeemed } = await api.post("/promo/redeem", { code: promoCode.trim(), project_id: projectId }, { params: { lang: getLocale() } });
      if (redeemed?.ok && redeemed?.project_id === projectId) await recordReportVersion(projectId, "promo");
      toast.success(t("results.promo_applied"));
      setPaidUnlocked(true);
      track("report_purchase", { method: "promo", project_id: projectId });
      navigate(`/report/${projectId}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || t("results.promo_failed");
      toast.error(msg);
    } finally {
      setPromoBusy(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
          <div className="flex items-start gap-3">
            {/* iter45ac — Back arrow: return to the Priority step (step 7,
                 zero-indexed 6) with sessionStorage.last_run.input preserved
                 so the buyer can tweak one input without restarting the
                 wizard. `resume=priority` tells Configurator to rehydrate
                 form from last_run.input and jump to step 6. */}
            <button
              type="button"
              onClick={() => navigate("/configurator?resume=priority")}
              className="btn-secondary shrink-0"
              style={{ padding: "8px 10px", marginTop: "6px" }}
              data-testid="btn-back-to-priority"
              title={t("results.back_to_priority_tip")}
              aria-label={t("results.back_to_priority_tip")}
            >
              <ArrowLeft size={14} /> {t("results.back_to_priority")}
            </button>
            <div>
              <span className="pill" style={{ background: "var(--paper)", color: "var(--brand-green)" }}>
                <ShieldCheck size={12} /> {t("results.tag")}
              </span>
              <h1 className="font-display text-3xl md:text-4xl mt-4">
                {t("results.title")}
              </h1>
              <Dialog onOpenChange={(o) => { if (o) track("how_it_works_open", { surface: "results" }); }}>
                <DialogTrigger asChild>
                  <button type="button" className="mt-2 inline-flex items-center gap-1.5 text-sm link" data-testid="results-how-link">
                    <Info size={14} /> {t("how.results_link")}
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-[720px] max-h-[85vh] overflow-y-auto" data-testid="results-how-dialog">
                  <DialogTitle className="font-display text-xl">{t("how.title")}</DialogTitle>
                  <HowItWorks variant="content" gridStatus={r?.inputs_echo?.grid_status || input.grid_status} />
                </DialogContent>
              </Dialog>
              <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
                {[sectorName(input.sector), applicationLabel(input), t("country." + input.country, input.country)].filter(Boolean).join(" · ")} · {t("results.head.peak")} {fmt(input.peak_kw)}{locale === "fr" ? "\u00a0" : " "}kW · {t("results.head.firm_target")} {fmt(m.firm_capacity_target_kw)}{locale === "fr" ? "\u00a0" : " "}kW ·
                {" "}{t("results.priority_label")} <span className="font-medium">{t(`results.priority.${canonicalPriority(input.priority)}`)}</span>
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setReconfirmOpen(true)} className="btn-secondary" data-testid="btn-reconfigure">
              <RotateCcw size={13} /> {t("results.reconfigure")}
            </button>
            <button onClick={save} disabled={busySave} className="btn-secondary" data-testid="btn-save">
              <Save size={13} /> {savedProjectId ? t("results.saved") : (busySave ? t("results.saving") : t("results.save"))}
            </button>
            <button onClick={() => setTalkOpen(true)} className="btn-primary" data-testid="btn-talk">
              <PhoneCall size={13} /> {t("results.talk")}
            </button>
          </div>
        </div>

        {/* Prompt J follow-through (Feb 2026) — Frontier strip. Renders
             the winner for each of the four priorities. The active-priority
             cell fills instantly from the loaded /run result; the other
             three fill from a single /api/configurator/frontier call
             (cached server-side per input hash). Spinner while pending,
             never a placeholder number. */}
        {r?.no_viable_candidate && (
          <div data-testid="results-not-viable-banner"
            style={{ background: "#FEE2E2", color: "#7F1D1D", padding: "12px 16px", borderRadius: 8, fontSize: 14, marginBottom: 12 }}>
            {(getLocale() === "fr" && r?.no_viable_reason?.fr) || r?.no_viable_reason?.en || r?.talk_to_engineer_reason}
          </div>
        )}
        <FrontierStrip input={input} result={r} />

        {/* iter45ad — Firm-capacity guardrail banner. Fires when the mix
             fails the 20% reserve margin (engine.firm_capacity_ok=false OR
             firm_available < firm_target × 0.98). Case: peak=43,200 →
             firm_target=51,840; 9 × 5,000 diesel = 45,000 kW → trips. */}
        {(() => {
          const target = Number(m?.firm_capacity_target_kw || 0);
          const available = Number(m?.firm_capacity_available_kw || 0);
          const insufficient = target > 0 && (m?.firm_capacity_ok === false || available < target * 0.98);
          if (!insufficient || r?.no_viable_candidate) return null;
          const bodyText = t("results.firm_shortfall_body")
            .replace("{available}", fmt(available))
            .replace("{target}", fmt(target))
            .replace("{peak}", fmt(input.peak_kw));
          return (
            <section
              className="card p-5 mb-6"
              style={{ background: "#FDECEC", borderColor: "#B00020", borderWidth: "1.5px" }}
              data-testid="firm-shortfall-banner"
            >
              <div className="overline" style={{ color: "#B00020" }}>{t("results.firm_shortfall_overline")}</div>
              <h3 className="font-display text-lg mt-1" style={{ color: "#B00020" }} data-testid="firm-shortfall-title">
                ⚠ {t("results.firm_shortfall_title")}
              </h3>
              <p className="text-sm mt-2" style={{ color: "#5a1416", lineHeight: 1.55 }} data-testid="firm-shortfall-body">
                {bodyText}
              </p>
            </section>
          );
        })()}

        {/* iter45ah Fix 5b — Unmet-load banner. Distinct diagnosis from firm-
             capacity: firm-capacity = instantaneous peak coverage fails (kW
             deficit at the moment of peak). Unmet-load = energy shortfall
             OVER TIME (kWh/yr the dispatch could not serve, e.g. long night
             with drained battery + genset can't cover full load). Sourced
             from Fix 2a's dispatch model (model.unmet_kwh_yr). */}
        {(() => {
          const unmet = Number(m?.unmet_kwh_yr || 0);
          const annual = Number(m?.annual_energy_kwh || 0);
          if (unmet < 1000 || r?.no_viable_candidate) return null; // ignore rounding-level noise; not-viable: the banner carries the reason
          const unmet_mwh = Math.round(unmet / 1000);
          const pct = annual > 0 ? Math.round((unmet / annual) * 100) : 0;
          return (
            <section
              className="card p-5 mb-6"
              style={{ background: "#FFF4E6", borderColor: "#B45309", borderWidth: "1.5px" }}
              data-testid="unmet-load-banner"
            >
              <div className="overline" style={{ color: "#B45309" }} data-testid="unmet-load-overline">
                {t("results.unmet_load_overline")}
              </div>
              <h3 className="font-display text-lg mt-1" style={{ color: "#B45309" }} data-testid="unmet-load-title">
                ⚠ {t("results.unmet_load_title")}
              </h3>
              <p className="text-sm mt-2" style={{ color: "#7a4408", lineHeight: 1.55 }} data-testid="unmet-load-body">
                {t("results.unmet_load_body")
                  .replace("{unmet_mwh}", num(unmet_mwh, 3))
                  .replace("{pct}", String(pct))}
              </p>
            </section>
          );
        })()}

        {/* iter45ao Prompt-H Step 2 — Conditional-fuel-supply banner.
             Fires when the buyer left gas_availability as `unknown` AND
             the scored candidate depends on gas. Amber, phrased as
             "confirm before advancing", not as a shortfall. Distinct
             from unmet-load and firm-capacity banners; can co-fire. */}
        {r?.conditional_on_fuel_confirmation && (
          <section
            className="card p-5 mb-6"
            style={{ background: "#FFF4E6", borderColor: "#B45309", borderWidth: "1.5px" }}
            data-testid="cond-fuel-banner"
          >
            <div className="overline" style={{ color: "#B45309" }} data-testid="cond-fuel-overline">
              {t("results.cond_fuel_overline")}
            </div>
            <h3 className="font-display text-lg mt-1" style={{ color: "#B45309" }} data-testid="cond-fuel-title">
              ⚠ {t("results.cond_fuel_title")}
            </h3>
            <p className="text-sm mt-2" style={{ color: "#7a4408", lineHeight: 1.55 }} data-testid="cond-fuel-body">
              {t("results.cond_fuel_body")}
            </p>
          </section>
        )}

        {/* iter45al — Stale-override notice. Shows when a returning buyer's
             saved edit could not be honored by the current engine. Never
             silently drops — explicit list per drop-with-notice policy. */}
        {staleOverrides.length > 0 && (
          <section
            className="card p-5 mb-6"
            style={{ background: "#FFF4E6", borderColor: "#B45309", borderWidth: "1.5px" }}
            data-testid="stale-overrides-banner"
          >
            <div className="overline" style={{ color: "#B45309" }}>
              {t("results.stale_overrides_overline")}
            </div>
            <h3 className="font-display text-lg mt-1" style={{ color: "#B45309" }}
                 data-testid="stale-overrides-title">
              ⚠ {t("results.stale_overrides_title").replace("{n}", String(staleOverrides.length))}
            </h3>
            <ul className="text-sm mt-2 space-y-1" style={{ color: "#7a4408", lineHeight: 1.55 }}
                 data-testid="stale-overrides-list">
              {staleOverrides.map((s, i) => (
                <li key={i} data-testid={`stale-override-${s.field || i}`}>
                  <strong>{s.field}:</strong> {s.reason}
                </li>
              ))}
              <li className="mt-1" style={{ opacity: 0.85 }}>
                {t("results.stale_overrides_footer")}
              </li>
            </ul>
            <button
              className="btn-secondary !py-1 !px-3 text-xs mt-3"
              onClick={() => setStaleOverrides([])}
              data-testid="stale-overrides-dismiss"
            >
              {t("results.stale_overrides_dismiss")}
            </button>
          </section>
        )}

        {/* Interactive: Adjust the mix (Prompt B) */}
        <AdjustMixPanel
          runId={runId}
          projectId={savedProjectId}
          input={input}
          recommended={r}
          onAdjusted={handleAdjusted}
          applyRequest={applyTweakRequest}
          language={locale}
          onSaveConfig={handleSaveConfig}
          savedAt={configSavedAt}
          usePlantRequest={usePlantRequest}
        />
        <MixCompare recommended={r} adjusted={adjustedResult}
                     language={locale} emissionFactors={emissionFactors} />

        {/* In-app feedback (Prompt C.3) */}
        <FeedbackWidget projectId={savedProjectId} runId={runId}
                          context={{ country: input?.country, sector: input?.sector }}
                          language={locale} />

        {/* Block 1: Optimised power model (FREE) */}
        <section className="card p-6 md:p-8 mb-6" data-testid="block-model">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="overline">01 · {t("results.block.1")}</div>
              <h2 className="font-display text-2xl mt-1 inline-flex items-center gap-2">
                {t("results.block.1_sub")}
                {/* iter45h — "Why this size?" popover in the block header.
                     Lists every thermal pick's auto reasoning so lenders
                     can trace the same rationale that the paid PDF prints
                     without scrolling to the Adjust panel. Rendered only
                     when the recommended mix carries thermal picks. */}
                {recTilePicks.length > 0 && (
                  <WhySizePopover
                    testid="rec-mix-why"
                    ariaLabel={locale === "fr" ? "Pourquoi cette taille ?" : "Why this size?"}
                    picks={recTilePicks}
                    language={locale}
                  />
                )}
              </h2>
            </div>
            <span className="pill" style={{ background: "#e8f5e9", color: "var(--brand-green)" }}>{t("results.free_preview")}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {!r?.no_viable_candidate && <div>
              <div style={{ width: "100%", height: 260 }} data-testid="chart-mix">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <PieChart>
                    <Pie data={mixData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                      {mixData.map((d) => <Cell key={d.name} fill={d.color} stroke="none" />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${fmt(v)} kWh${perYr()}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="text-center mt-2 text-sm" style={{ color: "var(--muted)" }}>
                {t("results.annual_served")} <span className="font-mono" style={{ color: "var(--charcoal)" }}>{fmt(m.annual_served_kwh || m.annual_energy_kwh)} kWh</span> ·
                {" "}{t("results.renewable_fraction")} <span className="font-mono" style={{ color: "var(--charcoal)" }}>{pct(Math.round((m.renewable_fraction || 0) * 100), 0)}</span>
              </div>
            </div>}

            <div className="grid grid-cols-2 gap-3">
              {/* One viability verdict: a not-viable result shows no plant and no ✓. */}
              {r?.no_viable_candidate ? (
                <div className="col-span-2" data-testid="tiles-not-viable">
                  <MixTile k={locale === "fr" ? "Aucune centrale viable" : "No viable plant"}
                           v={locale === "fr" ? "Non viable" : "Not viable"}
                           sub={locale === "fr" ? "voir le motif ci-dessus" : "see reason above"}
                           color="#B00020" testid="tile-not-viable" />
                </div>
              ) : (<>
              {m.solar_kw > 0 && <MixTile k={t("results.tile.solar")} v={`${fmt(m.solar_kw)}${getLocale() === "fr" ? "\u00a0kWc" : " kWp"}`} color="#65B037" testid="tile-solar" />}
              {m.battery_kwh > 0 && <MixTile k={t("results.tile.battery")} v={`${fmt(m.battery_kwh)} kWh / ${fmt(m.battery_power_kw)} kW`} color="#4CA6A8" testid="tile-battery" />}
              {m.genset_kw > 0 && <MixTile k={t(m.genset_role === "blackstart" ? "results.tile.diesel_blackstart" : "results.tile.diesel")} v={`${m.genset_units} × ${m.genset_unit_size_kw} kW`} color="#33403B" />}
              {/* Prompt K §8.3 — an OCGT/CCGT winner showed only Firm capacity
                  and Genset derate: the machine itself had no tile. */}
              {(m.ocgt_kw || 0) > 0 && <MixTile k={t("results.tile.ocgt")} v={`${m.gas_units || 0} × ${unit((m.gas_unit_size_kw || 0) / 1000, "MW", 0, 0)} · ${unit(m.ocgt_kw, "kW", 3)}`} color="#8A6A3B" testid="tile-ocgt" />}
              {(m.ccgt_kw || 0) > 0 && <MixTile k={t("results.tile.ccgt")} v={`${m.gas_units || 0} × ${unit((m.gas_unit_size_kw || 0) / 1000, "MW", 0, 0)} · ${unit(m.ccgt_kw, "kW", 3)}`} color="#6A5B8A" testid="tile-ccgt" />}
              {m.wind_kw > 0 && <MixTile k={t("results.tile.wind")} v={`${fmt(m.wind_kw)} kW`} color="#1A6F2B" />}
              {m.hfo_kw > 0 && <MixTile k={t("results.tile.hfo")} v={`${fmt(m.hfo_kw)} kW`} color="#6E7A74" />}
              {m.gas_kw > 0 && <MixTile k={t("results.tile.gas")} v={`${fmt(m.gas_kw)} kW`} color="#F6611C" testid="tile-gas" />}
              {/* F3 — derated verdict, nameplate stated beside it. */}
              <MixTile k={t("results.tile.firm")} v={`${fmt(m.firm_capacity_available_kw)} kW ${firmTileOk(m) ? "✓" : "!"}`} sub={firmBasisPair(m, locale) ? `${fmt(m.firm_capacity_installed_kw)} kW ${locale === "fr" ? "installés" : "installed"}` : null} color={firmTileOk(m) ? "#65B037" : "#F6611C"} testid="tile-firm" />
              <MixTile k={t("results.tile.derate")} v={pct(generatorDeratePct(m), 1, 0)} color="#6E7A74" />
              </>)}
            </div>
            {/* iter45ao Step 5 — Firm capacity build-up card, unconditional.
                Reads m.firm_capacity_buildup rebound by Step 1+5 from the
                scored candidate's own arithmetic. Status pill: red / amber /
                green. Amber fires exactly when unmet_kwh_yr > 0. */}
            {m.firm_capacity_buildup && !r?.no_viable_candidate && (
              <div className="card p-4 mt-4" data-testid="firm-capacity-buildup"
                   style={{background:"#F8F5F0",borderLeft:"3px solid #33403B"}}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-display" style={{color:"var(--ink)"}}>
                    {t("results.firm_buildup_title")}
                  </h4>
                  {firmPill(m).tone && (
                  <span data-testid="firm-status-pill"
                        data-firm-tone={firmPill(m).tone}
                        style={{
                          background: FIRM_PILL_BG[firmPill(m).tone] || "#E5E7EB",
                          color:      FIRM_PILL_FG[firmPill(m).tone] || "#374151",
                          padding:"3px 10px",borderRadius:12,fontWeight:600,fontSize:11
                        }}>
                    {t(firmPill(m).key)}
                  </span>)}
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {[
                      ["results.firm_dispatchable_kw", "dispatchable_kw"],
                      ["results.firm_battery_derated_kw", "battery_derated_kw"],
                      ["results.firm_available_kw", "available_kw"],
                      ["results.firm_target_kw", "target_kw"],
                      [m.battery_kwh > 0 ? "results.firm_low_sun_kw" : "results.firm_low_sun_kw_nobatt", "low_sun_stress_kw"],
                    ].map(([labelKey, dataKey]) => (
                      <tr key={dataKey} data-testid={`firm-row-${dataKey.replace(/_/g,"-")}`}>
                        <td className="py-1 pr-4" style={{color:"var(--muted)"}}>{t(labelKey)}</td>
                        <td className="py-1 font-mono text-right">
                          {fmt(m.firm_capacity_buildup[dataKey])} kW
                        </td>
                      </tr>
                    ))}
                    {m.battery_power_kw > 0 && (
                      <tr data-testid="firm-row-battery-rated-kw">
                        <td className="py-1 pr-4" style={{color:"var(--muted)"}}>{t("results.firm_battery_rated_kw")}</td>
                        <td className="py-1 font-mono text-right">{fmt(m.battery_power_kw)} kW</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {/* F3 — the verdict above is on the derated plant; the
                    nameplate and the conditions are stated beside it. */}
                {firmBasisNote(m, locale) && (
                  <p className="text-xs mt-3" style={{ color: "var(--muted)", lineHeight: 1.5 }}
                     data-testid="firm-basis-note">
                    {firmBasisNote(m, locale)}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Block 1b: Your Configuration — mirrors the Recommended mix block
            using the recalculated model from AdjustMixPanel. Rendered only
            after the buyer clicks Recalculate. The Recommended block above
            stays untouched so the customer can compare visually. */}
        {adjustedResult && (() => {
          const am = adjustedResult.model || {};
          const adjustedMixData = Object.entries(am.energy_per_source_kwh || {})
            .filter(([, v]) => v > 0)
            .map(([k, v]) => ({ name: sourceName(k), value: v, color: MIX_COLORS[k] || "#999" }));
          // iter45h — Popover picks for the adjusted Gas tile. Diff each
          // adjusted pick against the recommended baseline so the green
          // buyer notes fire when count / MW-per-unit differ.
          const adjustedThermalPicks = am.thermal_extras?.picks || [];
          const adjTilePicks = buildThermalMixPicks(adjustedThermalPicks, thermalBaselinePicks, locale);
          return (
            <section className="card p-6 md:p-8 mb-6" data-testid="block-model-adjusted">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                  <div className="overline">01b · {t("results.block.1b")}</div>
                  <h2 className="font-display text-2xl mt-1 inline-flex items-center gap-2">
                    {t("results.block.1b_title")}
                    {/* iter45h — Same popover on the adjusted card so
                         lenders can trace what changed in one click. */}
                    {adjTilePicks.length > 0 && (
                      <WhySizePopover
                        testid="adj-mix-why"
                        ariaLabel={locale === "fr" ? "Pourquoi cette taille ?" : "Why this size?"}
                        picks={adjTilePicks}
                        language={locale}
                      />
                    )}
                  </h2>
                </div>
                <span className="pill" style={{ background: "#FFE7C4", color: "#873F00" }} data-testid="pill-your-config">
                  {t("results.adjusted_mix")}
                </span>
              </div>

              {/* D3 — name what the Δ chips compare against. */}
              <div className="text-xs -mt-4 mb-6" style={{ color: "var(--muted)" }}
                   data-testid="adjusted-delta-basis">
                {recIsFullThermal && recPlant
                  ? `${t("results.adjusted.delta_basis")} ${plantLabel(recPlant, locale)}`
                  : t("results.adjusted.delta_basis_generic")}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div style={{ width: "100%", height: 260 }} data-testid="chart-mix-adjusted">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                      <PieChart>
                        <Pie data={adjustedMixData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                          {adjustedMixData.map((d) => <Cell key={d.name} fill={d.color} stroke="none" />)}
                        </Pie>
                        <Tooltip formatter={(v) => `${fmt(v)} kWh${perYr()}`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="text-center mt-2 text-sm" style={{ color: "var(--muted)" }}>
                    {t("results.annual_served")} <span className="font-mono" style={{ color: "var(--charcoal)" }} data-testid="adjusted-annual-energy">{fmt(am.annual_served_kwh || am.annual_energy_kwh)} kWh</span> ·
                    {" "}{t("results.renewable_fraction")} <span className="font-mono" style={{ color: "var(--charcoal)" }} data-testid="adjusted-renewable-fraction">{pct(Math.round((am.renewable_fraction || 0) * 100), 0)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {am.solar_kw > 0 && <MixTile k={t("results.tile.solar")} v={`${fmt(am.solar_kw)}${getLocale() === "fr" ? "\u00a0kWc" : " kWp"}`} color="#65B037" delta={deltaChip(am.solar_kw, m.solar_kw)} testid="adj-tile-solar" />}
                  {am.battery_kwh > 0 && <MixTile k={t("results.tile.battery")} v={`${fmt(am.battery_kwh)} kWh / ${fmt(am.battery_power_kw)} kW`} color="#4CA6A8" delta={deltaChip(am.battery_kwh, m.battery_kwh)} testid="adj-tile-battery" />}
                  {am.genset_kw > 0 && <MixTile k={t(am.genset_role === "blackstart" ? "results.tile.diesel_blackstart" : "results.tile.diesel")} v={`${am.genset_units} × ${am.genset_unit_size_kw} kW`} color="#33403B" delta={deltaChip(am.genset_kw, m.genset_kw)} testid="adj-tile-diesel" />}
                  {(am.ocgt_kw || 0) > 0 && <MixTile k={t("results.tile.ocgt")} v={`${am.gas_units || 0} × ${unit((am.gas_unit_size_kw || 0) / 1000, "MW", 0, 0)} · ${unit(am.ocgt_kw, "kW", 3)}`} color="#8A6A3B" delta={deltaChip(am.ocgt_kw, m.ocgt_kw)} testid="adj-tile-ocgt" />}
                  {(am.ccgt_kw || 0) > 0 && <MixTile k={t("results.tile.ccgt")} v={`${am.gas_units || 0} × ${unit((am.gas_unit_size_kw || 0) / 1000, "MW", 0, 0)} · ${unit(am.ccgt_kw, "kW", 3)}`} color="#6A5B8A" delta={deltaChip(am.ccgt_kw, m.ccgt_kw)} testid="adj-tile-ccgt" />}
                  {am.wind_kw > 0 && <MixTile k={t("results.tile.wind")} v={`${fmt(am.wind_kw)} kW`} color="#1A6F2B" delta={deltaChip(am.wind_kw, m.wind_kw)} testid="adj-tile-wind" />}
                  {am.hfo_kw > 0 && <MixTile k={t("results.tile.hfo")} v={`${fmt(am.hfo_kw)} kW`} color="#6E7A74" delta={deltaChip(am.hfo_kw, m.hfo_kw)} testid="adj-tile-hfo" />}
                  {am.gas_kw > 0 && <MixTile k={t("results.tile.gas")} v={`${fmt(am.gas_kw)} kW`} color="#F6611C" delta={deltaChip(am.gas_kw, m.gas_kw)} testid="adj-tile-gas" />}
                  <MixTile k={t("results.tile.firm")} v={`${fmt(am.firm_capacity_available_kw)} kW ${firmTileOk(am) ? "✓" : "!"}`} sub={firmBasisPair(am, locale) ? `${fmt(am.firm_capacity_installed_kw)} kW ${locale === "fr" ? "installés" : "installed"}` : null} color={firmTileOk(am) ? "#65B037" : "#F6611C"} delta={deltaChip(am.firm_capacity_available_kw, m.firm_capacity_available_kw)} testid="adj-tile-firm" />
                  <MixTile k={t("results.tile.derate")} v={pct(generatorDeratePct(am), 1, 0)} color="#6E7A74" delta={deltaChipPP(am.genset_derate_applied, m.genset_derate_applied)} testid="adj-tile-derate" />
                </div>
              </div>
            </section>
          );
        })()}

        {/* iter45v Fix 4 — Full-thermal baseload SCENARIO (not blended anymore).
             iter45w adds three lender-facing enhancements inside the scenario
             compare block: (1) buyer-flippable Preferred toggle on both cards
             (persisted to sessionStorage so the paid PDF picks it up),
             (2) 20-yr TCO bar chart to counter LCOE-only fixation, (3) side-
             by-side operational CO₂/yr with a climate-delta chip. */}
        {(r?.model?.thermal_extras) && (() => {
          const te = r.model.thermal_extras;
          const totals = te.totals || {};
          const picks = te.picks || [];
          const tb = r?.scenarios?.thermal_baseload;
          // iter45aq — the ENGINE decides whether this recommendation is a
          // hybrid at all (`solar + wind == 0 and battery == 0`, or a
          // renewable capacity share under the admin floor). We read that
          // verdict; we do not restate the rule here, or the card and the
          // PDF would eventually disagree.
          const scenarioVerdict = r?.scenarios?.recommended?.kind || "recommended_hybrid";
          const comparisonSuppressed = !!r?.scenarios?.comparison_suppressed;
          const preferred = preferredScenario;
          const hybridCapex = Number(cap.total || 0);
          const hybridLcoe = Number(fin.lcoe_usd_per_kwh || 0);
          const hybridOpex = Number(opex.annual || 0);
          const hybridRen = Number(m.renewable_fraction || 0);
          // iter45as4c commit (iii) — ONE cost kernel authors these figures
          // (D19), and `scenarios.thermal_baseload` is the object it wrote
          // them to. The old `|| totals.capex_usd_all_in` / `|| totals.annual_*`
          // fallbacks read Path B's own arithmetic and are gone.
          const tbCapex = Number(tb?.capex?.total || 0);
          // iter45y — Gas-price slider scaling. The engine's response has
          // `thermal_extras.fuel_gas.delivered_price_per_gj` as the price
          // this result was computed at (source of truth). The slider
          // preview scales fuel cost linearly against that; on release we
          // trigger an authoritative recompute (see effect below) that
          // replaces the numbers via `data` state.
          const enginePrice = Number(te?.fuel_gas?.delivered_price_per_gj || 8.0);
          const previewPrice = gasPricePreview != null ? gasPricePreview : enginePrice;
          const livePrice = gasPriceLive != null ? gasPriceLive : enginePrice;
          const scalingFactor = enginePrice > 0 ? previewPrice / enginePrice : 1.0;
          // Scale thermal-only fuel cost; leave var_om + fixed_om fixed.
          const tbFuelCost = Number(tb?.opex?.fuel_cost || 0);
          const tbVarOm = Number(tb?.opex?.var_om || 0);
          const tbFixedOm = Number(tb?.opex?.fixed_om || 0);
          const tbOpexScaled = tbFuelCost * scalingFactor + tbVarOm + tbFixedOm;
          const tbOpex = tbOpexScaled;
          // ONE TCO — the LCOE and both 20-year costs are the engine's figures, never recomputed here.
          const tbLcoe = Number(tb?.financials?.lcoe_usd_per_kwh || 0);
          const tbFirm = Number(tb?.model?.firm_capacity_available_kw || 0);
          const hybridTCO = Number(fin.tco_20yr_usd || 0);
          const thermalTCO = Number(tb?.financials?.tco_20yr_usd || 0);
          const maxTCO = Math.max(hybridTCO, thermalTCO, 1);
          const hybridCO2 = hybridCO2TonnesYr(r);
          const thermalCO2 = thermalCO2TonnesYr(r);
          const maxCO2 = Math.max(hybridCO2 ?? 0, thermalCO2, 1);
          // iter45as4c commit (iii) — the CO2 DELTA chip is gone (D17a). Both
          // FIGURES stay: what went is the self-comparison between them.
          return (
            <section className="card p-6 md:p-8 mb-6" data-testid="block-thermal">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="overline">{t("results.thermal.overline")}</div>
                  <h2 className="font-display text-2xl mt-1">{t("results.thermal.title")}</h2>
                  <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
                    {t("results.thermal.sub")}
                  </p>
                  {(r?.conditional_on_fuel_confirmation || data?.input?.gas_availability === "unknown") && (
                    <p className="text-sm mt-1" style={{ color: "#B45309" }} data-testid="gas-supply-unconfirmed">
                      {t("results.thermal.gas_unconfirmed").replace("{price}", perUnit(enginePrice, "GJ", 1))}
                    </p>
                  )}
                  {tb && r?.scenarios?.thermal_scenario_plant?.tech && (() => {
                    return (
                      <div className="mt-3" data-testid="use-plant-block">
                        <button type="button" className="btn-secondary !py-2 !px-3 text-sm"
                                data-testid="use-plant-in-config-btn"
                                onClick={() => setUsePlantRequest({ id: Date.now(), plant: r.scenarios.thermal_scenario_plant })}>
                          {t("results.thermal.use_plant")}
                        </button>
                        <p className="text-xs mt-1.5" style={{ color: "var(--muted)" }} data-testid="use-plant-note">
                          {t("results.thermal.use_plant_note")}
                        </p>
                      </div>
                    );
                  })()}
                </div>
                <span className="pill" style={{ background: "#FFE7C4", color: "#873F00" }} data-testid="thermal-scenario-pill">{t("results.thermal.scenario_pill")}</span>
              </div>

              {/* iter45aq — no head-to-head when the recommendation IS the
                  full-thermal baseload. The card says so rather than the
                  comparison quietly disappearing. */}
              {comparisonSuppressed && (
                <div className="mb-6 p-4 rounded-lg border" style={{ borderColor: "#873F00", background: "var(--paper)" }} data-testid="scenario-compare-suppressed">
                  <div className="text-sm font-semibold mb-1" style={{ color: "#873F00" }}>
                    {t("results.thermal.full_thermal_recommended")}
                  </div>
                  <div className="text-xs" style={{ color: "var(--muted)" }}>
                    {t("results.thermal.suppressed_reason")}
                  </div>
                </div>
              )}

              {/* Scenario head-to-head comparison — free tier, both LCOEs visible. */}
              {tb && !comparisonSuppressed && (
                <div className="mb-6 p-4 rounded-lg border" style={{ borderColor: "var(--hairline)", background: "var(--paper)" }} data-testid="scenario-compare">
                  <div className="text-sm font-semibold mb-1" style={{ color: "var(--charcoal)" }}>
                    {t("results.thermal.compare_title")}
                  </div>
                  <div className="text-xs mb-4" style={{ color: "var(--muted)" }}>
                    {t("results.thermal.compare_sub")}
                    {recIsFullThermal && (
                      <span className="block mt-1" data-testid="scenario-compare-two-plants">
                        {t("results.thermal.compare_two_plants")}
                        {recPlant ? ` ${plantLabel(recPlant, locale)}` : ""}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Hybrid card */}
                    <div className={`p-4 rounded border-2 ${preferred === "recommended" ? "" : "opacity-90"}`}
                         style={{ borderColor: preferred === "recommended" ? "#1A6F2B" : "var(--hairline)", background: "var(--white)" }}
                         data-testid="scenario-hybrid">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold" style={{ color: "var(--charcoal)" }}>
                          {t("results.thermal.hybrid")}
                        </div>
                        {preferred === "recommended" ? (
                          <span className="pill" style={{ background: "#E8F5E9", color: "#1A6F2B" }} data-testid="preferred-hybrid">{t("results.thermal.preferred_flag")}</span>
                        ) : (
                          <button
                            type="button"
                            className="pill hover:opacity-80"
                            style={{ background: "var(--paper)", color: "#1A6F2B", border: "1px solid #1A6F2B", cursor: "pointer" }}
                            data-testid="set-preferred-hybrid"
                            onClick={() => flipPreferred("recommended")}
                          >
                            {t("results.thermal.set_preferred")}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><div style={{ color: "var(--muted)" }}>{t("results.thermal.metric_capex")}</div><div className="font-mono font-semibold" style={{ color: "var(--charcoal)" }}>{usd(hybridCapex)}</div></div>
                        <div><div style={{ color: "var(--muted)" }}>{t("results.thermal.metric_lcoe")}</div><div className="font-mono font-semibold" style={{ color: "var(--charcoal)" }} data-testid="scenario-hybrid-lcoe">{perUnit(hybridLcoe, "kWh", 3)}</div></div>
                        <div><div style={{ color: "var(--muted)" }}>{t("results.thermal.metric_annual_opex")}</div><div className="font-mono" style={{ color: "var(--charcoal)" }}>{usd(hybridOpex)}</div></div>
                        <div><div style={{ color: "var(--muted)" }}>{t("results.thermal.metric_renewable")}</div><div className="font-mono" style={{ color: "var(--charcoal)" }}>{pct(Math.round(hybridRen * 100), 0)}</div></div>
                      </div>
                    </div>
                    {/* Thermal baseload card */}
                    <div className={`p-4 rounded border-2 ${preferred === "thermal_baseload" ? "" : "opacity-90"}`}
                         style={{ borderColor: preferred === "thermal_baseload" ? "#873F00" : "var(--hairline)", background: "var(--white)" }}
                         data-testid="scenario-thermal">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold" style={{ color: "var(--charcoal)" }}>
                          {t(tb?.source === "buyer_selection" ? "results.thermal.buyer_selection" : "results.thermal.baseload")}
                        </div>
                        {preferred === "thermal_baseload" ? (
                          <span className="pill" style={{ background: "#FFE7C4", color: "#873F00" }} data-testid="preferred-thermal">{t("results.thermal.preferred_flag")}</span>
                        ) : (
                          <button
                            type="button"
                            className="pill hover:opacity-80"
                            style={{ background: "var(--paper)", color: "#873F00", border: "1px solid #873F00", cursor: "pointer" }}
                            data-testid="set-preferred-thermal"
                            onClick={() => flipPreferred("thermal_baseload")}
                          >
                            {t("results.thermal.set_preferred")}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><div style={{ color: "var(--muted)" }}>{t("results.thermal.metric_capex")}</div><div className="font-mono font-semibold" style={{ color: "var(--charcoal)" }} data-testid="scenario-thermal-capex">{usd(tbCapex)}</div></div>
                        <div><div style={{ color: "var(--muted)" }}>{t("results.thermal.metric_lcoe")}</div><div className="font-mono font-semibold" style={{ color: "var(--charcoal)" }} data-testid="scenario-thermal-lcoe">{perUnit(tbLcoe, "kWh", 3)}</div></div>
                        <div><div style={{ color: "var(--muted)" }}>{t("results.thermal.metric_annual_opex")}</div><div className="font-mono" style={{ color: "var(--charcoal)" }}>{usd(tbOpex)}</div></div>
                        <div><div style={{ color: "var(--muted)" }}>{t("results.thermal.firm_capacity")}</div><div className="font-mono" style={{ color: "var(--charcoal)" }}>{fmt(tbFirm)} kW</div></div>
                      </div>
                    </div>
                  </div>

                  {/* iter45w — 20-yr TCO bar chart. Two horizontal bars on a
                       shared scale so lenders spot lifetime-cost winner at a
                       glance regardless of who owns the lower LCOE.
                       iter45as4c commit (iii) — the "pays back in Y yr" chip
                       that sat under the bars is REMOVED (D17a/D18). */}
                  <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--hairline)" }} data-testid="scenario-npv">
                    <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
                      <div className="text-xs font-semibold" style={{ color: "var(--charcoal)" }}>{t("results.thermal.npv_title")}</div>
                      <div className="text-[10px]" style={{ color: "var(--muted)" }}>{rateNote(t("results.thermal.npv_sub"), fin)}</div>
                    </div>
                    <div className="space-y-2 mt-2">
                      {[
                        { key: "hybrid", label: t("results.thermal.hybrid"), value: hybridTCO, color: "#1A6F2B", testid: "npv-hybrid" },
                        { key: "thermal", label: t(tb?.source === "buyer_selection" ? "results.thermal.buyer_selection" : "results.thermal.baseload"), value: thermalTCO, color: "#873F00", testid: "npv-thermal" },
                      ].map((b) => (
                        <div key={b.key} className="flex items-center gap-3">
                          <div className="text-xs w-40 shrink-0" style={{ color: "var(--muted)" }}>{b.label}</div>
                          <div className="flex-1 h-5 rounded-sm relative overflow-hidden" style={{ background: "#f3ede0" }}>
                            <div
                              className="absolute inset-y-0 left-0 flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(4, (b.value / maxTCO) * 100)}%`, background: b.color, transition: "width 400ms ease" }}
                            >
                              <span className="text-[10px] font-mono font-semibold text-white whitespace-nowrap" data-testid={b.testid}>{usd(b.value)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* iter45w — Side-by-side operational CO₂/yr. */}
                  <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--hairline)" }} data-testid="scenario-emissions">
                    <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                      <div>
                        <div className="text-xs font-semibold" style={{ color: "var(--charcoal)" }}>{t("results.thermal.emissions_title")}</div>
                        <div className="text-[10px]" style={{ color: "var(--muted)" }}>{t("results.thermal.emissions_sub")}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded" style={{ background: "#f8faf6", border: "1px solid #d8e7d8" }} data-testid="emissions-hybrid">
                        <div className="text-[10px] uppercase tracking-wider" style={{ color: "#1A6F2B" }}>{t("results.thermal.hybrid")}</div>
                        <div className="font-mono text-lg font-semibold mt-1" style={{ color: "var(--charcoal)" }}>{hybridCO2 == null ? "—" : fmt(Math.round(hybridCO2))}</div>
                        <div className="text-[10px]" style={{ color: "var(--muted)" }}>{t("results.thermal.tonnes_yr")}</div>
                        <div className="h-1 rounded-sm mt-2" style={{ background: "#e6efe6" }}>
                          <div className="h-full rounded-sm" style={{ width: `${((hybridCO2 ?? 0) / maxCO2) * 100}%`, background: "#1A6F2B" }} />
                        </div>
                      </div>
                      <div className="p-3 rounded" style={{ background: "#fdf6ee", border: "1px solid #e7d1b3" }} data-testid="emissions-thermal">
                        <div className="text-[10px] uppercase tracking-wider" style={{ color: "#873F00" }}>{t(tb?.source === "buyer_selection" ? "results.thermal.buyer_selection" : "results.thermal.baseload")}</div>
                        <div className="font-mono text-lg font-semibold mt-1" style={{ color: "var(--charcoal)" }}>{fmt(Math.round(thermalCO2))}</div>
                        <div className="text-[10px]" style={{ color: "var(--muted)" }}>{t("results.thermal.tonnes_yr")}</div>
                        <div className="h-1 rounded-sm mt-2" style={{ background: "#f2e0c9" }}>
                          <div className="h-full rounded-sm" style={{ width: `${(thermalCO2 / maxCO2) * 100}%`, background: "#873F00" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* iter45y — Gas price slider. Local scaling gives an
                       instant preview during drag; on release we call
                       /api/configurator/recompute for engine-perfect
                       numbers (dispatch mix can shift on higher prices,
                       not just thermal fuel cost). */}
                  {enginePrice > 0 && (
                    <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--hairline)" }} data-testid="scenario-gas-slider">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <div className="text-xs font-semibold" style={{ color: "var(--charcoal)" }}>
                          {t("results.thermal.gas_slider_label")} <span className="font-mono" data-testid="gas-slider-value">{perUnit(previewPrice, "GJ", 1)}</span>
                        </div>
                        {previewPrice !== livePrice && (
                          <span className="pill" style={{ background: "#FFF8F4", color: "#873F00" }} data-testid="gas-slider-preview-pill">
                            {gasPriceRecomputing ? t("results.thermal.gas_slider_recalculating") : t("results.thermal.gas_slider_preview")}
                          </span>
                        )}
                        {previewPrice !== enginePrice && (
                          <button
                            type="button"
                            className="pill hover:opacity-80"
                            style={{ background: "var(--paper)", color: "var(--charcoal)", border: "1px solid var(--hairline)", cursor: "pointer", fontSize: "10px" }}
                            data-testid="gas-slider-reset"
                            onClick={() => {
                              setGasPricePreview(enginePrice);
                              setGasPriceLive(enginePrice);
                              // Restore live numbers by dropping any adjusted overrides is out of
                              // scope — the reset just returns the slider handle to the engine
                              // price so the on-screen numbers realign with the last live result.
                            }}
                          >
                            ↺ {t("results.thermal.gas_reset")}
                          </button>
                        )}
                      </div>
                      <input
                        type="range" min="2" max="30" step="0.5"
                        value={previewPrice}
                        data-testid="gas-slider-input"
                        onChange={(e) => {
                          const newPrice = Number(e.target.value);
                          setGasPricePreview(newPrice);
                          // iter45z — 250 ms debounce. Clears any pending
                          // recompute and schedules a new one so aggressive
                          // sweeps only hit the backend once at the end.
                          if (gasPriceDebounceRef.current) {
                            clearTimeout(gasPriceDebounceRef.current);
                          }
                          gasPriceDebounceRef.current = setTimeout(() => {
                            runGasPriceRecompute(newPrice);
                          }, 250);
                        }}
                        style={{ width: "100%", accentColor: "#873F00" }}
                      />
                      <div className="flex justify-between text-[10px]" style={{ color: "var(--muted)" }}>
                        <span>$2/GJ</span><span>$30/GJ</span>
                      </div>
                      {/* iter45aa — Rate-card preset chips. Click snaps to a
                           realistic price and fires an immediate recompute
                           (bypasses the 250 ms debounce — the click is a
                           deliberate commit, not a drag tick). */}
                      <div className="flex flex-wrap gap-2 mt-3" data-testid="gas-slider-presets">
                        {[
                          { price: 5,  label: t("results.thermal.preset_gulf") },
                          { price: 10, label: t("results.thermal.preset_wafrica") },
                          { price: 18, label: t("results.thermal.preset_lng") },
                        ].map((preset) => {
                          const isActive = Math.abs(previewPrice - preset.price) < 0.01;
                          return (
                            <button
                              key={preset.price}
                              type="button"
                              className="pill hover:opacity-80"
                              style={{
                                background: isActive ? "#873F00" : "var(--white)",
                                color: isActive ? "#F7F5EE" : "#873F00",
                                border: "1px solid #873F00",
                                cursor: "pointer",
                                fontSize: "10px",
                              }}
                              data-testid={`gas-preset-${preset.price}`}
                              onClick={() => {
                                if (gasPriceDebounceRef.current) {
                                  clearTimeout(gasPriceDebounceRef.current);
                                  gasPriceDebounceRef.current = null;
                                }
                                setGasPricePreview(preset.price);
                                runGasPriceRecompute(preset.price);
                              }}
                            >
                              ${preset.price}/GJ · {preset.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* iter45y — Scenario notes (buyer rationale). Session
                       only until the project is saved. Included in the
                       save payload + compare PDF. */}
                  <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--hairline)" }} data-testid="scenario-notes">
                    <label className="text-xs font-semibold block mb-1" style={{ color: "var(--charcoal)" }} htmlFor="scenario-notes-input">
                      {t("results.thermal.notes_label")}
                    </label>
                    <textarea
                      id="scenario-notes-input"
                      data-testid="scenario-notes-input"
                      className="w-full text-sm p-2 rounded font-sans"
                      style={{ background: "var(--white)", border: "1px solid var(--hairline)", minHeight: "60px", resize: "vertical" }}
                      placeholder={t("results.thermal.notes_placeholder")}
                      value={scenarioNotes}
                      onChange={(e) => updateNotes(e.target.value)}
                      maxLength={500}
                    />
                    <div className="text-[10px] mt-1 text-right" style={{ color: "var(--muted)" }} data-testid="scenario-notes-count">
                      {scenarioNotes.length} / 500 {t("results.thermal.notes_char_count")}
                    </div>
                  </div>

                  {/* iter45x + iter45y — CSV + Save-as-PDF actions row. */}
                  <div className="mt-4 pt-3 border-t flex items-center justify-end gap-2 flex-wrap" style={{ borderColor: "var(--hairline)" }}>
                    <button
                      type="button"
                      className="pill hover:opacity-80"
                      style={{ background: "var(--paper)", color: "var(--charcoal)", border: "1px solid var(--hairline)", cursor: "pointer" }}
                      data-testid="scenario-csv-download"
                      onClick={() => {
                        // Build a CSV: metric | <the engine's verdict for the
                        // recommendation> | full_thermal_baseload. Numbers are
                        // raw (no formatting) so finance models can consume
                        // without regex-strip.
                        const rows = [
                          ["metric", scenarioVerdict, "full_thermal_baseload"],
                          ["capex_all_in_usd", hybridCapex, tbCapex],
                          ["annual_opex_usd", hybridOpex, tbOpex],
                          ["lcoe_usd_per_kwh", hybridLcoe, tbLcoe],
                          ["renewable_share_pct", Math.round(hybridRen * 100), 0],
                          ["firm_capacity_kw", Number(m.firm_capacity_available_kw || 0), tbFirm],
                          ["annual_energy_kwh", Number(m.annual_energy_kwh || 0), Number(tb?.model?.annual_energy_kwh || 0)],
                          ["tco_20yr_usd", Math.round(hybridTCO), Math.round(thermalTCO)],
                          ["op_co2_tonnes_per_yr", hybridCO2 == null ? null : Math.round(hybridCO2), Math.round(thermalCO2)],
                          ["preferred_for_report", preferred === "recommended" ? "yes" : "no",
                                                     preferred === "thermal_baseload" ? "yes" : "no"],
                        ];
                        const csv = rows.map(r =>
                          r.map(cell => {
                            const s = String(cell ?? "");
                            return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                          }).join(",")
                        ).join("\n");
                        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `scenario-comparison-${(input?.project_name || "project").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(a.href);
                      }}
                    >
                      ↓ {t("results.thermal.csv_download")}
                    </button>
                    <SaveCompareBtn
                      buildBody={() => ({
                        input,
                        result: (() => {
                          const cloned = JSON.parse(JSON.stringify(r || {}));
                          if (cloned.scenarios) {
                            cloned.scenarios.preferred = preferredScenario;
                            cloned.scenarios.notes = scenarioNotes;
                          }
                          return cloned;
                        })(),
                        language: locale,
                      })}
                      slug={(input?.project_name || "scenario").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}
                      t={t}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                <TileKV k={t("results.thermal.total_capex")} v={usd(totals.capex_usd_all_in || totals.capex_usd)} testid="thermal-total-capex" />
                <TileKV k={t("results.thermal.cf")} v={thermalCF(picks) == null ? "—" : pct(Math.round(thermalCF(picks) * 100), 0)} testid="thermal-total-cf" />
                <TileKV k={t("results.thermal.fuel_cost")} v={usd(totals.annual_fuel_cost_usd)} testid="thermal-total-fuel" />
                <TileKV k={t("results.thermal.generation")} v={`${fmt(totals.annual_energy_kwh)} kWh`} testid="thermal-total-energy" />
                <TileKV k={t("results.thermal.cod")} v={totals.months_to_cod ? `${totals.months_to_cod} ${t("results.thermal.mo")}` : "—"} testid="thermal-total-cod" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {picks.map((p, i) => (
                  <div key={i} className="p-4 rounded border" style={{ borderColor: "var(--hairline)", background: "var(--white)" }} data-testid={`thermal-pick-${p.tech}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold" style={{ color: "var(--charcoal)" }}>
                        {p.tech === "gas_engine" ? t("results.thermal.gas_engine") : p.tech.toUpperCase()}
                      </div>
                      <div className="text-xs font-mono" style={{ color: "var(--muted)" }}>
                        {p.count} × {unit(p.unit_mw, "MW", 3)}
                      </div>
                    </div>
                    <div className="mt-1.5 font-display text-lg">{unit(p.derated_mw, "MW", 3)} <span className="text-xs" style={{ color: "var(--muted)" }}>{t("results.thermal.derated")}</span></div>
                    <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                      η {pct(Math.round((p.eff_lhv || 0) * 100), 0)} · {t("results.thermal.lead")} {p.lead_time_months} {t("results.thermal.mo")} + {t("results.thermal.build")} {p.construction_months} {t("results.thermal.mo")}
                      {p.water_required && ` · ${t("results.thermal.water")}`}
                    </div>
                    <ThermalPickLines p={p} demandKwh={Number(r?.model?.annual_demand_kwh || 0)} t={t} prefix="thermal" />
                    {p.recovered_heat_kwh_yr > 0 && (
                      <div className="text-xs mt-1" style={{ color: "#1A6F2B" }}>
                        CHP: {unit(p.recovered_heat_kwh_yr, `kWh${perYr()}`)} {t("results.thermal.chp_recovered")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {(r.notes?.thermal_warnings || []).length > 0 && (
                <div className="mt-4 p-3 rounded text-xs" style={{ background: "#FFF8F4", color: "#873F00" }} data-testid="thermal-warnings">
                  {r.notes.thermal_warnings.map((w, i) => (<div key={i}>{w}</div>))}
                </div>
              )}
            </section>
          );
        })()}

        {/* Adjusted thermal configuration (iter44d). Mirrors the recommended
             thermal block but reads from adjustedResult and shows a Δ chip
             on every tile so the buyer sees how much each knob moved. Only
             renders when the recompute produced a thermal_extras block AND
             at least one pick differs from the recommended (skips the
             visually-noisy "±0% everywhere" state). */}

        {adjustedResult?.model?.thermal_extras && (() => {
          // Prompt K §5 — a full-thermal recommendation carries no
          // `thermal_extras` of its own (the wizard sent no picks), so the
          // card used to be unreachable on exactly the case that needs it.
          const rt = r?.model?.thermal_extras || {};
          const at = adjustedResult.model.thermal_extras;
          const rtot = rt.totals || {};
          const atot = at.totals || {};
          const inMix = Boolean(adjustedResult?.notes?.thermal_in_mix);
          const thermalUnmet = Number(atot.unmet_kwh_yr || 0);
          const thermalWarnings = adjustedResult?.notes?.thermal_warnings || [];
          const rPicksByTech = Object.fromEntries((rt.picks || []).map((p) => [p.tech, p]));
          const changed = (at.picks || []).some((p) => {
            const rp = rPicksByTech[p.tech];
            return !rp || rp.count !== p.count || rp.unit_mw !== p.unit_mw;
          }) || (rt.picks || []).length !== (at.picks || []).length;
          if (!changed) return null;
          return (
            <section className="card p-6 md:p-8 mb-6" data-testid="block-thermal-adjusted">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="overline">{t("results.thermal_adj.overline")}</div>
                  <h2 className="font-display text-2xl mt-1">{t("results.thermal_adj.title")}</h2>
                  <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
                    {t("results.thermal_adj.sub")}
                  </p>
                  <p className="text-sm mt-2 font-medium"
                      style={{ color: inMix ? "var(--brand-green)" : "#873F00" }}
                      data-testid="adj-thermal-scope">
                    {t(inMix ? "results.thermal_adj.scope_in_mix"
                             : "results.thermal_adj.scope_separate")}
                  </p>
                </div>
                <span className="pill" style={{ background: "#FFE7C4", color: "#873F00" }}>{t("results.thermal_adj.pill")}</span>
              </div>
              {thermalUnmet > 0 && (
                <div className="mb-4 p-3 rounded" style={{ background: "#FFF4E6", border: "1px solid #B45309" }}
                      data-testid="adj-thermal-unmet">
                  <span className="text-sm font-semibold" style={{ color: "#B45309" }}>
                    {t("results.thermal_adj.unmet")} {unit(thermalUnmet, `kWh${perYr()}`)}
                  </span>
                  {thermalWarnings.length > 0 && (
                    <ul className="mt-2 text-xs" style={{ color: "#7a4408" }} data-testid="adj-thermal-warnings">
                      {thermalWarnings.map((w, i) => <li key={i}>• {w}</li>)}
                    </ul>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                {/* §8.2 — this tile showed TECH-ONLY CAPEX beside the
                     recommended card's all-in figure, so a same-plant
                     recalculation read "−42 %". All-in on both sides. */}
                <TileKV k={t("results.thermal.total_capex_allin")}
                          v={usd(atot.capex_usd_all_in || atot.capex_usd)} testid="adj-thermal-total-capex"
                          delta={deltaChip(atot.capex_usd_all_in || atot.capex_usd,
                                            rtot.capex_usd_all_in || rtot.capex_usd)} />
                <TileKV k={t("results.thermal.cf")}
                          v={thermalCF(at.picks) == null ? "—" : pct(Math.round(thermalCF(at.picks) * 100), 0)}
                          testid="adj-thermal-total-cf"
                          delta={deltaChipPP(thermalCF(at.picks), thermalCF(rt.picks))} />
                <TileKV k={t("results.thermal.fuel_cost")} v={usd(atot.annual_fuel_cost_usd)} testid="adj-thermal-total-fuel"
                          delta={deltaChip(atot.annual_fuel_cost_usd, rtot.annual_fuel_cost_usd)} />
                <TileKV k={t("results.thermal.generation")} v={`${fmt(atot.annual_energy_kwh)} kWh`} testid="adj-thermal-total-energy"
                          delta={deltaChip(atot.annual_energy_kwh, rtot.annual_energy_kwh)} />
                <TileKV k={t("results.thermal.cod")} v={atot.months_to_cod ? `${atot.months_to_cod} ${t("results.thermal.mo")}` : "—"} testid="adj-thermal-total-cod"
                          delta={deltaChipPP(atot.months_to_cod, rtot.months_to_cod, t("results.thermal.mo"))} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(at.picks || []).map((p, i) => {
                  const rp = rPicksByTech[p.tech];
                  return (
                    <div key={i} className="p-4 rounded border" style={{ borderColor: "var(--hairline)", background: "var(--white)" }} data-testid={`adj-thermal-pick-${p.tech}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold" style={{ color: "var(--charcoal)" }}>
                          {p.tech === "gas_engine" ? t("results.thermal.gas_engine") : p.tech.toUpperCase()}
                        </div>
                        {(() => {
                          const d = rp ? deltaChip(p.count, rp.count) : { label: "New", bg: "#E4F3E7", fg: "#1A6F2B", title: "Not in recommended" };
                          if (!d) return null;
                          return (
                            <span
                              className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded"
                              style={{ background: d.bg, color: d.fg }}
                              data-testid={`adj-thermal-pick-${p.tech}-delta`}
                              title={d.title}
                            >
                              {d.label}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="mt-1.5 font-display text-lg">{unit(p.derated_mw, "MW", 3)} <span className="text-xs" style={{ color: "var(--muted)" }}>{t("results.thermal.derated")}</span></div>
                      <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                        {p.count} × {unit(p.unit_mw, "MW", 3)} {rp && rp.count !== p.count && <span style={{ color: "var(--brand-green)" }}>({rp.count} → {p.count})</span>}
                      </div>
                      <ThermalPickLines p={p} demandKwh={Number(adjustedResult?.model?.annual_demand_kwh || 0)} t={t} prefix="adj-thermal" />
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })()}

        {/* Free Cost Range + Carbon Footprint side-by-side (iter44m) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <section className="card p-6 md:p-8" data-testid="block-cost-range">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="overline">{t("results.range.title")}</div>
              {r?.no_viable_candidate ? <NvNote locale={locale} testid="cost-range-not-viable" /> : (<>
              <h2 className="font-display text-2xl mt-1">{usd(cap.range_low)} – {usd(cap.range_high)}</h2>
              <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
                {t("results.range.central")} {usd(cap.total)} · {t("results.range.region_mult")} {cap.region_multiplier}
              </p>
              </>)}
            </div>
            <span className="pill" style={{ background: "#e8f5e9", color: "var(--brand-green)" }}>{t("results.free_preview")}</span>
          </div>
          {!r?.no_viable_candidate && (<>
          <div className="mt-4 h-2 rounded relative" style={{ background: "var(--paper)" }}>
            <div className="absolute h-2 rounded" style={{ left: "0%", width: "100%", background: "linear-gradient(90deg, var(--brand-bright), var(--brand-green), var(--orange-spark))" }} />
          </div>
          <div className="mt-2 flex justify-between text-xs font-mono" style={{ color: "var(--muted)" }}>
            <span>{usd(cap.range_low)} — {t("results.range.low")}</span>
            <span>{usd(cap.total)} — {t("results.range.central_short")}</span>
            <span>{usd(cap.range_high)} — {t("results.range.high")}</span>
          </div>
          </>)}
        </section>

        {/* iter44m — Carbon footprint score card. Sits beside the CAPEX
             range and updates automatically when the buyer clicks
             Recalculate on the Adjust-the-mix panel (adjustedResult drives
             the "Your configuration" row). */}
        {r?.no_viable_candidate ? (
          <section className="card p-6 md:p-8" data-testid="block-carbon-score">
            <div className="overline">{locale === "fr" ? "Empreinte carbone estimée" : "Estimated carbon footprint"}</div>
            <NvNote locale={locale} testid="carbon-not-viable" />
          </section>
        ) : (
        <CarbonScoreCard recommended={r} adjusted={adjustedResult} onTryTweak={handleTryTweak} t={t} />
        )}

        </div>

        {/* §12 COD (FREE). ONE date, read from the engine's schedule — the
             same author as v3 §12, the memo and the exports. The full phase
             programme stays in the paid report. */}
        {sched.cod_month && !r?.no_viable_candidate ? (
          <section className="card p-6 md:p-8 mb-6" data-testid="block-cod">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="overline">{t("results.cod.title")}</div>
                <h2 className="font-display text-2xl mt-1" data-testid="cod-month">
                  {t("results.cod.month")} {sched.cod_month}
                </h2>
                <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
                  {t("results.cod.sub")}
                </p>
                {codLeadNote && (
                  <p className="text-xs mt-2" style={{ color: "var(--muted)" }} data-testid="cod-lead-note">
                    {codLeadNote}
                  </p>
                )}
              </div>
              <span className="pill" style={{ background: "#e8f5e9", color: "var(--brand-green)" }}>{t("results.free_preview")}</span>
            </div>
          </section>
        ) : null}

        {/* Sensitivity */}
        <div className="mb-6">
          {r?.no_viable_candidate ? (
            <section className="card p-6 md:p-8" data-testid="sensitivity-not-viable-card">
              <div className="overline">{locale === "fr" ? "Sensibilité" : "Sensitivity"}</div>
              <NvNote locale={locale} testid="sensitivity-not-viable" />
            </section>
          ) : (
          <SensitivityPanel baselineInput={input} baselineResult={r} />
          )}
        </div>

        {/* iter45ab Fix 8 — Talk-to-an-engineer panel. Always rendered so
             every scope has the escalation path for custom / complex
             architectures we do not auto-configure (e.g. solar + CCGT). */}
        <section
          className="card p-6 md:p-8 mb-6"
          style={{
            background: "linear-gradient(135deg, #233333 0%, #3a4a4a 100%)",
            borderColor: "#233333",
          }}
          data-testid="talk-to-engineer-panel"
        >
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div className="max-w-2xl">
              <div className="font-display text-2xl mb-2" style={{ color: "#F7F5EE" }} data-testid="tte-title">
                {t("results.tte.title")}
              </div>
              <div className="text-sm" style={{ color: "#e4e6dd", lineHeight: 1.55 }}>
                {t("results.tte.body")}
              </div>
            </div>
            <a
              href={`mailto:${t("results.tte.email")}`}
              className="pill font-mono hover:opacity-90"
              style={{ background: "#F7F5EE", color: "#233333", padding: "10px 18px", fontSize: "13px", fontWeight: 600, textDecoration: "none" }}
              data-testid="tte-cta"
            >
              ✉ {t("results.tte.cta")}
            </a>
          </div>
        </section>

        {/* a06f — a run with no comparison says why, once, before the buyer pays. */}
        {isNotScored(fin.callout) && fin.callout?.reason === "counterfactual_not_scored" && (
          <section className="card p-6 md:p-8 mb-6" data-testid="no-comparison-note">
            <p className="text-sm leading-relaxed" data-testid="no-comparison-note-text">{noComparisonLong(locale, { adjusted: !!adjustedResult, dieselAvailability: input?.diesel_availability })}</p>
          </section>
        )}

        {/* a07 — no viable plant: nothing to buy, and the screen says why instead of a payment box. */}
        {!paidUnlocked && r?.no_viable_candidate && (
          <section className="card p-6 md:p-8 mb-6" data-testid="no-viable-no-purchase">
            <p className="text-sm leading-relaxed" data-testid="no-viable-no-purchase-text">{t("results.paywall.no_viable")}</p>
          </section>
        )}

        {/* Paywall CTA */}
        {!paidUnlocked && !r?.no_viable_candidate && (
          <section className="card p-6 md:p-8 mb-6" style={{ background: "linear-gradient(135deg, #f5f7f4 0%, #ffffff 100%)", borderColor: "var(--brand-green)" }} data-testid="paywall-cta">
            <div className="flex flex-wrap items-center gap-6 justify-between">
              <div className="max-w-lg">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} style={{ color: "var(--brand-green)" }} />
                  <div className="overline">{t("results.paywall.overline")}</div>
                </div>
                <h3 className="font-display text-2xl mt-2">{t("results.paywall.title")}</h3>
                <p className="text-sm mt-2" style={{ color: "var(--body)" }}>
                  {t("results.paywall.desc")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {/* iter45ap 4b-ii — PaywallSafetyNet swaps CTA hierarchy
                    and auto-opens the unlock modal when the buyer already
                    holds credits, so paying customers aren't dumped onto
                    a "Buy report" wall they should never see. */}
                <PaywallSafetyNet
                  user={user}
                  project={{ id: savedProjectId, paid: paidUnlocked }}
                  pricing={pricing}
                  onOpenUnlockModal={() => setConfirmUnlockOpen(true)}
                  onBuyReport={(pkgId) => buyReport(pkgId)}
                  onUseCredit={unlockWithCredit}
                />
              </div>
            </div>
            {/* Promo code redemption row */}
            <div className="mt-6 pt-5 flex flex-wrap items-center gap-3" style={{ borderTop: "1px dashed var(--hairline)" }}>
              <div className="flex items-center gap-2" style={{ color: "var(--muted)" }}>
                <Ticket size={14} />
                <span className="text-sm font-medium">{t("results.paywall.promo_label")}</span>
              </div>
              <div className="flex-1 flex flex-wrap gap-2 min-w-[280px]">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === "Enter") redeemPromo(); }}
                  placeholder={t("results.paywall.promo_placeholder")}
                  disabled={promoBusy}
                  className="flex-1 min-w-[180px] px-3 py-2 text-sm font-mono uppercase tracking-wider"
                  style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}
                  data-testid="promo-code-input"
                />
                <button
                  onClick={redeemPromo}
                  disabled={promoBusy || !promoCode.trim()}
                  className="btn-secondary"
                  data-testid="promo-code-redeem"
                >
                  {promoBusy ? t("results.paywall.promo_applying") : t("results.paywall.promo_redeem")}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Locked/Paid blocks preview (blurred). A2 (30 Oct) — none on a site with no viable plant. */}
        {!r?.no_viable_candidate && (
        <div className={paidUnlocked ? "" : "locked"} data-testid="locked-preview">
          <section className="card p-6 md:p-8 mb-6">
            <div className="overline">02 · {t("results.locked.equipment")}</div>
            <h2 className="font-display text-2xl mt-1 mb-4">{t("results.locked.equipment_sub")}</h2>
            {r?.no_viable_candidate ? <NvNote locale={locale} testid="equipment-not-viable" /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(r.equipment || []).slice(0, 6).map(equipmentRow).map((e) => (
                <div key={`${e.category}-${e.item}`} className="p-3 rounded-md border" style={{ borderColor: "var(--hairline)" }}>
                  <div className="overline" style={{ fontSize: "0.6rem" }}>{e.category}</div>
                  <div className="text-sm font-medium mt-1" style={{ color: "var(--charcoal)" }}>{e.item}</div>
                  <div className="text-xs mt-1 font-mono" style={{ color: "var(--muted)" }}>{e.size}</div>
                </div>
              ))}
            </div>
            )}
          </section>

          <section className="card p-6 md:p-8 mb-6">
            <div className="overline">03 · {t("results.locked.costs")}</div>
            <h2 className="font-display text-2xl mt-1 mb-4">{t("results.locked.costs_sub")}</h2>
            {r?.no_viable_candidate ? <NvNote locale={locale} testid="costs-not-viable" /> : (<>
            <div className="h-56" style={{ width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart data={capexBars} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid stroke="#e2e7e2" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke="#6e7a74" style={{ fontSize: 11 }} tickFormatter={(v) => moneyK(v)} />
                  <YAxis dataKey="name" type="category" stroke="#6e7a74" width={140} style={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => usd(v)} />
                  <Bar dataKey="value" fill="#1A6F2B" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <KPI k={t("results.kpi.lcoe")} v={perUnit(fin.lcoe_usd_per_kwh, "kWh", 4)} />
              <KPI k={t("results.kpi.opex")} v={usd(opex.annual)} />
              <KPI k={t("results.kpi.payback")} v={comparisonYears(fin.simple_payback_years, locale)} hint={t("results.kpi.payback_hint")} />
              <KPI k={t("results.kpi.life_used")} v={locale === "fr" ? `${ANALYSIS_YEARS}\u00a0ans` : `${ANALYSIS_YEARS} yr`} />
            </div>
            </>)}
          </section>

          <section className="card p-6 md:p-8 mb-6">
            <div className="overline">04 · {t("results.locked.permitting")} · {t("country." + input.country, input.country)}</div>
            {(() => {
              const permits = localisePermitting(r.permitting, locale);
              return (
                <>
                  <h2 className="font-display text-2xl mt-1">
                    {permits?.regime}
                  </h2>
                  <ul className="mt-4 space-y-2">
                    {(permits?.items || []).slice(0, 4).map((it) => (
                      <li key={it.item} className="p-3 rounded-md border text-sm" style={{ borderColor: "var(--hairline)" }}>
                        <div className="flex justify-between flex-wrap gap-2">
                          <span className="font-medium" style={{ color: "var(--charcoal)" }}>{it.item}</span>
                          <span className="pill" style={{ background: "var(--paper)", color: "var(--muted)" }}>{it.status}</span>
                        </div>
                        {it.authority && <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{it.authority}</div>}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}
          </section>
        </div>
        )}

        {paidUnlocked && (
          <div className="card p-6 mb-6" style={{ background: "#e8f5e9", borderColor: "var(--brand-green)" }}>
            <div className="flex items-center gap-3">
              <FileText size={18} style={{ color: "var(--brand-green)" }} />
              <div>
                <div className="font-display text-lg">{t("results.report_unlocked")}</div>
                <button onClick={() => navigate(`/report/${savedProjectId}`)} className="btn-ghost !p-0 mt-1" data-testid="link-open-report">
                  {t("results.open_report")} <ArrowRight size={14} className="inline" />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="indicative-banner text-xs">
          {t("results.disclaimer")}
        </div>
      </div>
      <Footer />
      <TalkToEngineerModal open={talkOpen} onClose={() => setTalkOpen(false)} defaults={{ country: input.country, summary: `${[input.sector, applicationLabel(input)].filter(Boolean).join(" — ")} in ${input.country}, ${fmt(input.peak_kw)} kW peak.` }} projectId={savedProjectId} source="results" />
      {(() => {
        const tier = (pricing.tiers || []).find(t => t.package_id === payModal.pkg);
        const amountLabel = tier ? `${money(tier.price_usd, 3)}` : money(pricing.report);
        const packageName = tier ? tier.name : "Detailed report";
        return (
          <PaymentMethodModal
            open={payModal.open}
            onClose={() => setPayModal({ open: false, pkg: null, projectId: null })}
            packageId={payModal.pkg}
            projectId={payModal.projectId}
            amountLabel={amountLabel}
            packageName={packageName}
            onPaid={onPaid}
          />
        );
      })()}
      <PurchaseModal
        open={newPurchaseOpen}
        onClose={() => setNewPurchaseOpen(false)}
        lockedProjects={savedProjectId ? [{ id: savedProjectId, name: input.project_name || `${input.country} · ${input.sector}`, input }] : []}
        startProjectId={savedProjectId}
        onPurchaseComplete={({ choice }) => {
          setNewPurchaseOpen(false);
          setPaidUnlocked(true);
          track("report_purchase", { method: "paypal", package_id: choice === "single" ? "single_report" : "credit_bundle_5", project_id: savedProjectId });
          if (choice === "single" && savedProjectId) navigate(`/report/${savedProjectId}`);
        }}
      />
      <ConfirmUnlockModal
        open={confirmUnlockOpen}
        onClose={() => setConfirmUnlockOpen(false)}
        project={savedProjectId ? { id: savedProjectId, name: input.project_name || `${input.country} · ${input.sector}` } : null}
        walletBefore={user?.credits || 0}
        onUnlocked={() => { setPaidUnlocked(true); setConfirmUnlockOpen(false); if (savedProjectId) navigate(`/report/${savedProjectId}`); }}
      />
      {/* iter45ac — Reconfigure "Rebuild from scratch" confirmation. Cancel
           dismisses; Confirm clears the buyer's saved run + adjusted state
           and navigates to a fresh wizard at step 0. */}
      <AlertDialog open={reconfirmOpen} onOpenChange={setReconfirmOpen}>
        <AlertDialogContent data-testid="reconfigure-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw size={16} style={{ color: "#873F00" }} />
              {t("results.reconfirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("results.reconfirm_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reconfigure-confirm-cancel">
              {t("results.reconfirm_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reconfigure-confirm-ok"
              onClick={() => {
                try {
                  sessionStorage.removeItem("last_run");
                } catch (e) { /* no-op */ }
                setReconfirmOpen(false);
                navigate("/configurator");
              }}
            >
              {t("results.reconfirm_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const TileKV = ({ k, v, testid, delta }) => (
  <div className="p-3 rounded border" style={{ borderColor: "var(--hairline)", background: "var(--paper)" }} data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <div className="overline" style={{ fontSize: "0.62rem" }}>{k}</div>
      {delta && (
        <span
          className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded"
          style={{ background: delta.bg, color: delta.fg }}
          data-testid={testid ? `${testid}-delta` : undefined}
          title={delta.title}
        >
          {delta.label}
        </span>
      )}
    </div>
    <div className="font-display text-base mt-0.5">{v}</div>
  </div>
);

// One viability verdict: a plant-figure card on a not-viable result says so; the banner carries the reason.
const NvNote = ({ locale, testid }) => (
  <p className="font-display text-xl mt-2" style={{ color: "#B00020" }} data-testid={testid}>
    {locale === "fr" ? "Non viable — voir ci-dessus" : "Not viable — see above"}
  </p>
);

const MixTile = ({ k, v, color, delta, testid, sub = null }) => (
  <div className="p-4 rounded-md border" style={{ borderColor: "var(--hairline)", background: "var(--white)" }} data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 overline" style={{ fontSize: "0.62rem" }}>
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} /> {k}
      </div>
      {delta && (
        <span
          className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded"
          style={{ background: delta.bg, color: delta.fg }}
          data-testid={testid ? `${testid}-delta` : undefined}
          title={delta.title}
        >
          {delta.label}
        </span>
      )}
    </div>
    <div className="font-display text-lg mt-1">{v}</div>
    {/* F3 — the nameplate under a derated firm figure. */}
    {sub && (
      <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}
           data-testid={testid ? `${testid}-sub` : undefined}>{sub}</div>
    )}
  </div>
);

/** Percentage-delta chip for a quantity (kW, kWh). Handles the four edge cases:
 *  new component (0 → N), removed (N → 0), unchanged (0 → 0 or same value), and
 *  a normal +/- % change. Green when the value grew, amber when it shrank —
 *  buyers who deliberately dialled *down* a size still get a signal that isn't
 *  buried in identical-looking tiles. */
// Prompt K §7 — how hard the plant runs. Σ pick energy ÷ (Σ installed kW ×
// 8,760): it is why OCGT 3 × 20 MW burns more fuel per kWh than gas engines
// 5 × 6 MW for the same 175,200 MWh.
const thermalCF = (picks) => {
  const e = (picks || []).reduce((a, p) => a + Number(p.annual_energy_kwh || 0), 0);
  const k = (picks || []).reduce((a, p) => a + Number(p.installed_kw || 0), 0);
  return k > 0 ? e / (k * 8760) : null;
};

const ThermalPickLines = ({ p, demandKwh, t, prefix }) => {
  const cf = Math.round(Number(p.capacity_factor || 0) * 100);
  const serves = demandKwh > 0
    ? Math.round((Number(p.annual_energy_kwh || 0) / demandKwh) * 100) : null;
  const effL = Number(p.eff_lhv || 0);
  const effB = Number(p.eff_billed ?? effL);
  const lowShare = Number(p.low_load_hours_share || 0);
  const isDiesel = p.tech === "diesel";
  const lpk = Number(p.l_per_kwh_billed || 0);
  const locNum = (s) => (getLocale() === "fr" ? s.replace(".", ",") : s);
  const pct1 = (v) => locNum(num(v * 100, 1, 1));
  const num3 = (v) => locNum(num(v, 3, 3));
  return (
    <div className="text-xs mt-1 space-y-0.5" style={{ color: "var(--muted)" }}
          data-testid={`${prefix}-lines-${p.tech}`}>
      <div>{t("results.thermal.cf_line").split("{cf}").join(String(cf))}</div>
      {serves != null && (
        <div>{t("results.thermal.serves_line").replace("{pct}", String(serves))}</div>
      )}
      {(effB > 0 || lpk > 0) && (
        <div style={{ color: lowShare > 0 ? "#B45309" : undefined }} data-testid={`${prefix}-partload-${p.tech}`}>
          {t(`results.thermal.${isDiesel ? "partload_line_diesel" : "partload_line"}${lowShare > 0 ? "" : (isDiesel ? "_lpk" : "_eta")}`)
            .replace("{eta}", pct1(effB)).replace("{share}", pct1(lowShare)).replace("{lpk}", num3(lpk))}
        </div>
      )}
    </div>
  );
};

const deltaChip = (nextV, prevV) => {
  const n = Number(nextV || 0);
  const p = Number(prevV || 0);
  if (!p && !n) return null;
  if (!p && n > 0) return { label: "New", bg: "#E4F3E7", fg: "#1A6F2B", title: "Not in the recommended mix" };
  if (p > 0 && !n) return { label: "Removed", bg: "#FCE7D8", fg: "#873F00", title: "Removed vs recommended mix" };
  if (Math.abs(n - p) / p < 0.005) return { label: getLocale() === "fr" ? "±0\u00a0%" : "±0%", bg: "var(--paper)", fg: "var(--muted)", title: "Unchanged vs recommended mix" };
  const pct = Math.round(((n - p) / p) * 100);
  const up = pct >= 0;
  return {
    label: `${up ? "+" : ""}${pct}${getLocale() === "fr" ? "\u00a0" : ""}%`,
    bg: up ? "#E4F3E7" : "#FCE7D8",
    fg: up ? "#1A6F2B" : "#873F00",
    title: `${up ? "+" : ""}${pct}% vs recommended (${fmt(p)} → ${fmt(n)})`,
  };
};

/** Percentage-point (or unit) delta chip for values that aren't ratios —
 *  e.g. genset_derate_applied ∈ [0,1] (unit "pp") OR months-to-COD (unit
 *  "mo"). Rendered as `+Npp` or `+N mo` so engineers see the raw shift
 *  instead of a misleading percentage. */
const deltaChipPP = (nextV, prevV, unit = "pp") => {
  // For "pp" mode, both values are fractions we multiply by 100. For other
  // units (like "mo") we compare the raw values.
  const scale = unit === "pp" ? 100 : 1;
  const n = Math.round(Number(nextV || 0) * scale);
  const p = Math.round(Number(prevV || 0) * scale);
  if (n === p) return { label: `±0${unit}`, bg: "var(--paper)", fg: "var(--muted)", title: "Unchanged vs recommended" };
  const diff = n - p;
  const up = diff >= 0;
  return {
    label: `${up ? "+" : ""}${diff}${unit}`,
    bg: up ? "#E4F3E7" : "#FCE7D8",
    fg: up ? "#1A6F2B" : "#873F00",
    title: `${up ? "+" : ""}${diff} ${unit === "pp" ? "percentage points" : unit} vs recommended (${p}${unit === "pp" ? "%" : ""} → ${n}${unit === "pp" ? "%" : ""})`,
  };
};

const KPI = ({ k, v, hint }) => (
  <div className="p-4 rounded-md" style={{ background: "var(--paper)" }}>
    <div className="overline flex items-center gap-1" style={{ fontSize: "0.62rem" }}>
      {k}
      {hint && (
        <span className="relative group inline-flex" data-testid={`kpi-hint-${(k || "").toLowerCase().replace(/\s+/g, "-")}`}>
          <Info size={11} className="cursor-help opacity-60 hover:opacity-100" style={{ color: "var(--muted)" }} />
          <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-20 hidden group-hover:block w-64 p-3 rounded-md text-[11px] leading-[1.5] font-normal normal-case tracking-normal shadow-lg" style={{ background: "var(--charcoal)", color: "var(--white)", letterSpacing: 0 }}>
            {hint}
          </span>
        </span>
      )}
    </div>
    <div className="font-display text-lg mt-1">{v}</div>
  </div>
);
